import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { FiTrash2 } from 'react-icons/fi';
import { useAuthFetch } from '../hooks/useAuthFetch';
import { useFullscreenSheetModal } from '../hooks/useFullscreenSheetModal';
import { ConfirmDialog } from './common/ConfirmDialog';
import CrewTimesheetUpdateMultipleModal from './CrewTimesheetUpdateMultipleModal';
import CrewTravelUpdateMultipleModal from './CrewTravelUpdateMultipleModal';
import ModalEquipmentTimeCard from './ModalEquipmentTimeCard';
import CrewCopyFromDateCalendar from './CrewCopyFromDateCalendar';
import { DailyLogBackButton, DailyLogBreadcrumbs, DailyLogProjectFields } from './DailyLogFlowNav';
import './ModalCrewTimesheet.css';

/**
 * Crew Timesheet modal — Project Labor / Non-Project hours grid.
 * Save and Log Production pushes Project Labor totals only (not Non-Project) to Miter
 * for the current PP job + activity. Non-Project hours stay in the local grid for now.
 */

const getToday = () => new Date().toISOString().split('T')[0];

const formatWorkDateDisplay = (isoDate) => {
  const raw = String(isoDate || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw || '—';
  const [y, m, d] = raw.split('-');
  return `${m}-${d}-${y}`;
};

const NON_PROJECT_TYPES = [
  { id: 'ware-house', label: 'Warehouse' },
  { id: 'equip-repair', label: 'Equip. Repair' },
];

/** Non-Project column ids — never included in Miter hours for the current PP job/activity. */
const NON_PROJECT_COLUMN_IDS = new Set(NON_PROJECT_TYPES.map((c) => c.id));

const MILEAGE_REIMBURSEMENT_TYPES = [
  { id: 'per-vehicle-mileage', label: 'Per. Vehicle Mileage' },
  { id: 'non-cdl-drive', label: 'Non-CDL Drive' },
  { id: 'ab-cdl-drive', label: 'A/B CDL Drive' },
  { id: 'windshield', label: 'Windshield' },
];

const PER_DIEM_TYPES = [
  { id: 'room-alone', label: 'Room Alone' },
  { id: 'room-shared', label: 'Room Shared' },
  { id: 'employee-paid', label: 'Employee Paid' },
];

const TRAVEL_FLAG_IDS = [
  ...MILEAGE_REIMBURSEMENT_TYPES.map((c) => c.id),
  ...PER_DIEM_TYPES.map((c) => c.id),
];

/**
 * Client Rate Table (Reimbursement Table.xlsx).
 * Band is chosen from that row's Miles; Amount =
 * sum(checked mileage × $/mi × miles) + sum(checked per diem flat rates).
 */
const TRAVEL_MILEAGE_FLAG_IDS = MILEAGE_REIMBURSEMENT_TYPES.map((c) => c.id);
const TRAVEL_PER_DIEM_FLAG_IDS = PER_DIEM_TYPES.map((c) => c.id);

const toTravelMilesNumber = (value) => {
  if (value === '' || value == null) return 0;
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
};

/**
 * Client Rate Table (Reimbursement Table.xlsx).
 * Band is chosen from that row's Miles; Amount =
 * sum(checked mileage × $/mi × miles) + sum(checked per diem flat rates).
 */
const TRAVEL_RATE_BANDS = [
  {
    id: 'in-town',
    label: 'In-Town',
    mileage: {
      'per-vehicle-mileage': 0,
      'non-cdl-drive': 0.28,
      'ab-cdl-drive': 0.5,
      windshield: 0.16,
    },
    perDiem: {
      'room-alone': 0,
      'room-shared': 0,
      'employee-paid': 0,
    },
  },
  {
    id: 'range-1',
    label: 'Range 1',
    mileage: {
      'per-vehicle-mileage': 0.42,
      'non-cdl-drive': 0.28,
      'ab-cdl-drive': 0.5,
      windshield: 0.16,
    },
    perDiem: {
      'room-alone': 0,
      'room-shared': 50,
      'employee-paid': 100,
    },
  },
  {
    id: 'range-2',
    label: 'Range 2',
    mileage: {
      'per-vehicle-mileage': 0.42,
      'non-cdl-drive': 0.28,
      'ab-cdl-drive': 0.5,
      windshield: 0.16,
    },
    perDiem: {
      'room-alone': 25,
      'room-shared': 75,
      'employee-paid': 125,
    },
  },
  {
    id: 'range-3',
    label: 'Range 3',
    mileage: {
      'per-vehicle-mileage': 0.42,
      'non-cdl-drive': 0.28,
      'ab-cdl-drive': 0.5,
      windshield: 0.16,
    },
    perDiem: {
      'room-alone': 50,
      'room-shared': 100,
      'employee-paid': 150,
    },
  },
];

/** Pick rate band from row miles: <75, 75–250, 251–500, >500. */
const getTravelRateBand = (miles) => {
  const m = toTravelMilesNumber(miles);
  if (m < 75) return TRAVEL_RATE_BANDS[0];
  if (m <= 250) return TRAVEL_RATE_BANDS[1];
  if (m <= 500) return TRAVEL_RATE_BANDS[2];
  return TRAVEL_RATE_BANDS[3];
};

const buildEmptyTravelFlags = () =>
  Object.fromEntries(TRAVEL_FLAG_IDS.map((id) => [id, false]));

/**
 * Derive Amount from that row's miles + checked flags using the client rate bands.
 * Never uses header Miles From Branch at calc time — only row.miles.
 */
const recomputeTravelAmount = (row = {}) => {
  const miles = toTravelMilesNumber(row.miles);
  const flags = row.flags || {};
  const band = getTravelRateBand(miles);
  let total = 0;

  TRAVEL_MILEAGE_FLAG_IDS.forEach((flagId) => {
    if (!flags[flagId]) return;
    const rate = Number(band.mileage[flagId]) || 0;
    total += miles * rate;
  });

  TRAVEL_PER_DIEM_FLAG_IDS.forEach((flagId) => {
    if (!flags[flagId]) return;
    total += Number(band.perDiem[flagId]) || 0;
  });

  return Math.round(total * 100) / 100;
};

const withTravelAmount = (row) => ({
  ...row,
  amount: recomputeTravelAmount(row),
});

const buildEmptyTravelEntry = (defaultMiles = '0') =>
  withTravelAmount({
    overrideDefaultMiles: false,
    miles: String(defaultMiles ?? '0'),
    flags: buildEmptyTravelFlags(),
    miterIds: {},
  });

/** Normalize API / saved travel row into UI shape. */
const normalizeTravelEntry = (raw = {}, defaultMiles = '0') => {
  const flags = { ...buildEmptyTravelFlags(), ...(raw.flags || {}) };
  TRAVEL_FLAG_IDS.forEach((id) => {
    flags[id] = Boolean(flags[id]);
  });
  const override = Boolean(raw.overrideDefaultMiles ?? raw.override_default_miles);
  const milesRaw =
    raw.miles != null && raw.miles !== ''
      ? String(raw.miles)
      : String(defaultMiles ?? '0');
  const miterIds =
    raw.miterIds && typeof raw.miterIds === 'object' && !Array.isArray(raw.miterIds)
      ? { ...raw.miterIds }
      : raw.miter_ids &&
          typeof raw.miter_ids === 'object' &&
          !Array.isArray(raw.miter_ids)
        ? { ...raw.miter_ids }
        : {};
  return withTravelAmount({
    overrideDefaultMiles: override,
    miles: milesRaw,
    flags,
    miterIds,
  });
};

/** Same office origins used by Dashboard → Travel & Production miles. */
const TRAVEL_BRANCH_OFFICES = [
  {
    name: 'Waller, TX',
    address: '41229 park 290 Dr, waller, tx 77484',
    matchers: ['waller'],
  },
  {
    name: 'Kansas City, KS',
    address: '2850 fairfax trafficway, Kansas City, KS 66115',
    matchers: ['kansas city', 'kansas', 'kc'],
  },
];

function getSelectedProjectDetails() {
  try {
    const raw = localStorage.getItem('selectedProjectDetails');
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

/** Build project site address the same way as Production Performance travel miles. */
function buildProjectAddressText(projectData = {}) {
  const street = projectData.street || '';
  const city = projectData.city || '';
  const state = projectData.state || '';
  const zipCode = projectData.zipCode || '';
  if (street || city || state || zipCode) {
    const firstLine = street;
    const secondLine = [city, state, zipCode].filter(Boolean).join(', ');
    if (firstLine && secondLine) return `${firstLine}, ${secondLine}`;
    if (firstLine) return firstLine;
    if (secondLine) return secondLine;
  }
  if (projectData.projectAddress && projectData.projectAddress !== '-') {
    return String(projectData.projectAddress).trim();
  }
  return '';
}

function resolveTravelBranchOffice(companyLabel) {
  const raw = String(companyLabel || '').trim();
  if (!raw) return null;
  const exact = TRAVEL_BRANCH_OFFICES.find(
    (b) => b.name.toLowerCase() === raw.toLowerCase()
  );
  if (exact) return exact;
  const lower = raw.toLowerCase();
  return (
    TRAVEL_BRANCH_OFFICES.find((b) =>
      b.matchers.some((m) => lower.includes(m) || m.includes(lower))
    ) || null
  );
}

/** Default miles for a row: employee Work Location → office miles, else project branch miles. */
function getDefaultMilesForWorkLocation(
  workLocation,
  milesByWorkLocation = {},
  fallbackMiles = '0'
) {
  const key = String(workLocation || '').trim();
  if (key && milesByWorkLocation[key] != null && milesByWorkLocation[key] !== '') {
    return String(milesByWorkLocation[key]);
  }
  return String(fallbackMiles ?? '0');
}

/** "212 mi" / "1,234 mi" → "212" / "1234" */
function parseDistanceMiles(distanceText) {
  if (!distanceText || distanceText === 'N/A') return null;
  const m = String(distanceText).match(/[\d,]+(?:\.\d+)?/);
  if (!m) return null;
  return m[0].replace(/,/g, '');
}

const formatTravelAmount = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return '$0.00';
  return `$${v.toFixed(2)}`;
};

const getInitials = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
};

const isValidPartialHoursInput = (value) => value === '' || /^\d*\.?\d*$/.test(value);

const toNumber = (value) => {
  if (value === '' || value == null) return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const slugLaborLabel = (label = '') =>
  String(label || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/** Stable labor column ids (label/code based) so hours survive reload/reorder. */
const buildLaborColumnId = ({ label, descriptionCode, prefix = 'desc' }) => {
  const code = String(descriptionCode || '').trim();
  if (code) return `code-${code}`;
  const slug = slugLaborLabel(label);
  return slug ? `${prefix}-${slug}` : `${prefix}-unknown`;
};

const buildDefaultLaborColumnsFromDescriptions = (descriptions = []) =>
  (Array.isArray(descriptions) ? descriptions : [])
    .map((item) => {
      const text = String(
        typeof item === 'string' ? item : item?.label || item?.description || ''
      ).trim();
      if (!text) return null;
      const descriptionCode = String(
        typeof item === 'object' && item ? item.descriptionCode || item.code || '' : ''
      ).trim();
      return {
        id: buildLaborColumnId({
          label: text,
          descriptionCode,
          prefix: 'desc',
        }),
        label: text,
        descriptionCode: descriptionCode || null,
        isDefault: true,
      };
    })
    .filter(Boolean);

const findScopeEntry = (laborScopeCodesFromApi, scopeName) => {
  if (!scopeName || !Array.isArray(laborScopeCodesFromApi)) return null;
  const target = String(scopeName).trim().toLowerCase();
  return (
    laborScopeCodesFromApi.find(
      (e) => String(e.scope_name || '').trim().toLowerCase() === target
    ) || null
  );
};

const buildEmptyHours = (columnIds) => {
  const hours = {};
  columnIds.forEach((id) => {
    hours[id] = 0;
  });
  return hours;
};

/**
 * Map a saved hours object onto current columns.
 * Matches by column id first, then by labor label (case-insensitive).
 */
const mapSavedHoursToColumns = (savedHours, columns, labelBySavedKey = {}) => {
  const mapped = {};
  if (!savedHours || typeof savedHours !== 'object') return mapped;

  const columnsById = new Map(columns.map((c) => [String(c.id), c.id]));
  const columnsByLabel = new Map(
    columns.map((c) => [String(c.label || '').trim().toLowerCase(), c.id])
  );

  Object.entries(savedHours).forEach(([key, value]) => {
    if (value == null || value === '') return;
    const direct = columnsById.get(String(key));
    if (direct) {
      mapped[direct] = value;
      return;
    }
    const label = String(labelBySavedKey[key] || key || '')
      .trim()
      .toLowerCase();
    const byLabel = label ? columnsByLabel.get(label) : null;
    if (byLabel) {
      mapped[byLabel] = value;
    }
  });

  return mapped;
};

const buildEmployeesFromRoster = (
  projectLaborColumns,
  previousEmployees = [],
  rosterEmployees = [],
  savedHoursByEmployee = {},
  savedHourLabelsByEmployee = {}
) => {
  const columnIds = [
    ...projectLaborColumns.map((c) => c.id),
    ...NON_PROJECT_TYPES.map((c) => c.id),
  ];
  const allColumns = [...projectLaborColumns, ...NON_PROJECT_TYPES];

  return (rosterEmployees || [])
    .filter((e) => e?.id)
    .map((emp) => {
      const empId = String(emp.id);
      const prev = previousEmployees.find((e) => String(e.id) === empId);
      const savedHours = savedHoursByEmployee[empId] || null;
      const savedLabels = savedHourLabelsByEmployee[empId] || {};
      const hours = buildEmptyHours(columnIds);

      // Baseline from persisted Crew Timesheet grid (id or label match).
      if (savedHours) {
        const mapped = mapSavedHoursToColumns(savedHours, allColumns, savedLabels);
        Object.entries(mapped).forEach(([id, value]) => {
          hours[id] = value;
        });
      }

      // Preserve in-session edits when roster/columns rebuild (remap by id or label).
      if (prev?.hours) {
        const prevLabelHints = {
          ...Object.fromEntries(allColumns.map((c) => [c.id, c.label])),
          ...savedLabels,
        };
        const mappedPrev = mapSavedHoursToColumns(
          prev.hours,
          allColumns,
          prevLabelHints
        );
        Object.entries(mappedPrev).forEach(([id, value]) => {
          if (value == null || value === '') return;
          // A roster rebuild can still be holding the empty grid. Don't let
          // those zeros cover hours that just loaded.
          if (Number(value) === 0 && Number(hours[id]) > 0) return;
          // A Miter delete comes back as a saved 0. Don't put the old hours back.
          if (savedHours && Number(hours[id]) === 0 && Number(value) > 0) return;
          hours[id] = value;
        });
      }

      return {
        id: emp.id,
        name: emp.name || 'Unknown',
        hours,
        friendlyId: emp.friendlyId || null,
        title: emp.title || null,
        employmentStatus: emp.employmentStatus || null,
        employmentType: emp.employmentType || null,
        workLocation:
          emp.workLocation ||
          prev?.workLocation ||
          null,
      };
    });
};

function measureStickyHeaderWidth(scroller) {
  const stickyHeads = scroller?.querySelectorAll('thead tr:first-child th.sticky');
  if (!stickyHeads?.length) return 0;
  let width = 0;
  stickyHeads.forEach((th) => {
    width += th.offsetWidth;
  });
  return Math.ceil(width);
}

const TABLE_VISIBLE_BODY_ROWS = 8;
const TABLE_SCROLLBAR_GUTTER = 12;
/** Phones in landscape, mobile browser and installed PWA (not iPad). */
const SHORT_LANDSCAPE_QUERY = '(orientation: landscape) and (max-height: 600px)';
/** Body rows kept visible under the pinned header so the list can actually be scrolled. */
const LANDSCAPE_MIN_BODY_ROWS = 4;

function isShortLandscapeViewport() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia(SHORT_LANDSCAPE_QUERY).matches
  );
}

/**
 * Short landscape used to cap these tables at 46% of the screen. The two-row
 * header (and Crew Total, on the hours table) then left about two data rows,
 * which is too short to scroll. Fill the modal sheet instead, and never go
 * below four body rows.
 */
function landscapeTableScrollMax({ el, theadH, rowH, maxBodyRows, eightRowMax, footerEl }) {
  const sheet = el.closest('.nettles-flow-sheet-scroll');
  const sheetH = sheet?.clientHeight || 0;
  const footerH = footerEl?.offsetHeight || 0;
  const available =
    sheetH > 80
      ? sheetH - footerH - TABLE_SCROLLBAR_GUTTER
      : Math.round(window.innerHeight * 0.78) - footerH;
  const minRows = Math.min(LANDSCAPE_MIN_BODY_ROWS, maxBodyRows);
  const minUseful = theadH + rowH * minRows + TABLE_SCROLLBAR_GUTTER;
  return Math.min(eightRowMax, Math.max(minUseful, available));
}

/**
 * Nearest sheet scroller (portrait body, or the landscape sheet itself).
 * Stops at the modal panel so the page behind the dialog never moves.
 */
function scrollSheetBy(fromEl, deltaY) {
  if (!deltaY) return false;
  const boundary = fromEl?.closest?.('.nettles-flow-sheet-panel') || null;
  let node = fromEl?.parentElement;
  while (node && node !== boundary) {
    const { overflowY } = window.getComputedStyle(node);
    const canScrollY =
      overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
    if (canScrollY && node.scrollHeight > node.clientHeight + 1) {
      const maxScroll = node.scrollHeight - node.clientHeight;
      const atTop = node.scrollTop <= 0;
      const atBottom = node.scrollTop >= maxScroll - 1;
      if ((deltaY < 0 && atTop) || (deltaY > 0 && atBottom)) return false;
      node.scrollTop += deltaY;
      return true;
    }
    node = node.parentElement;
  }
  return false;
}

/** Hide native h-scrollbar; proxy bar starts after pinned columns (Tracking Details). */
function PinnedColumnHScroll({
  children,
  footer = null,
  measureKey,
  maxBodyRows = TABLE_VISIBLE_BODY_ROWS,
}) {
  const scrollerRef = useRef(null);
  const footerRef = useRef(null);
  const barRef = useRef(null);
  const touchStartRef = useRef(null);
  const [frozenWidth, setFrozenWidth] = useState(0);
  const [headRow1Height, setHeadRow1Height] = useState(0);
  const [maxHeight, setMaxHeight] = useState(null);
  const [hScroll, setHScroll] = useState({ overflow: 0, scrollWidth: 0, clientWidth: 0 });

  const syncLinkedScrollLeft = (next) => {
    const scroller = scrollerRef.current;
    const footerEl = footerRef.current;
    const bar = barRef.current;
    if (scroller && Math.abs(scroller.scrollLeft - next) >= 1) {
      scroller.scrollLeft = next;
    }
    if (footerEl && Math.abs(footerEl.scrollLeft - next) >= 1) {
      footerEl.scrollLeft = next;
    }
    if (bar && Math.abs(bar.scrollLeft - next) >= 1) {
      bar.scrollLeft = next;
    }
  };

  const syncFooterColumns = useCallback(() => {
    const scroller = scrollerRef.current;
    const footerEl = footerRef.current;
    if (!scroller || !footerEl) return;
    const srcRow = Array.from(scroller.querySelectorAll('tbody tr')).find(
      (tr) => !tr.querySelector('td[colspan]')
    );
    const srcCells = srcRow?.querySelectorAll('td');
    const dstTable = footerEl.querySelector('table');
    const dstCells = footerEl.querySelectorAll('td');
    if (!srcCells?.length || !dstCells.length || srcCells.length !== dstCells.length) {
      return;
    }
    const srcTable = scroller.querySelector('table');
    if (dstTable && srcTable) {
      dstTable.style.width = `${srcTable.scrollWidth}px`;
      dstTable.style.minWidth = `${srcTable.scrollWidth}px`;
    }
    srcCells.forEach((td, index) => {
      const width = `${td.offsetWidth}px`;
      dstCells[index].style.width = width;
      dstCells[index].style.minWidth = width;
      dstCells[index].style.maxWidth = width;
    });
  }, []);

  const hasPinnedFooter = Boolean(footer);

  const measure = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const scrollWidth = el.scrollWidth;
    const clientWidth = el.clientWidth;
    const overflow = Math.max(0, scrollWidth - clientWidth);
    const nextFrozen = measureStickyHeaderWidth(el);
    setFrozenWidth((prev) => (Math.abs(prev - nextFrozen) < 1 ? prev : nextFrozen));
    setHScroll((prev) => {
      if (
        prev.overflow === overflow &&
        prev.scrollWidth === scrollWidth &&
        prev.clientWidth === clientWidth
      ) {
        return prev;
      }
      return { overflow, scrollWidth, clientWidth };
    });

    const thead = el.querySelector('thead');
    const firstHeadRow = thead?.querySelector('tr');
    const nextHead1 = Math.ceil(firstHeadRow?.offsetHeight || 0);
    setHeadRow1Height((prev) => (Math.abs(prev - nextHead1) < 1 ? prev : nextHead1));

    const dataRows = Array.from(el.querySelectorAll('tbody tr')).filter(
      (tr) => !tr.querySelector('td[colspan]')
    );
    if (dataRows.length <= maxBodyRows) {
      setMaxHeight(null);
      syncFooterColumns();
      return;
    }

    const theadH = thead?.offsetHeight || 0;
    const rowH = dataRows[0]?.offsetHeight || 56;
    let nextMax = theadH + rowH * maxBodyRows + TABLE_SCROLLBAR_GUTTER;
    if (isShortLandscapeViewport()) {
      nextMax = landscapeTableScrollMax({
        el,
        theadH,
        rowH,
        maxBodyRows,
        eightRowMax: nextMax,
        footerEl: hasPinnedFooter ? footerRef.current : null,
      });
    }
    setMaxHeight((prev) => (prev != null && Math.abs(prev - nextMax) < 1 ? prev : nextMax));
    syncFooterColumns();
  }, [hasPinnedFooter, maxBodyRows, syncFooterColumns]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return undefined;

    let startX = 0;
    let startY = 0;
    let lastY = 0;
    let axis = null;

    const onTouchStart = (event) => {
      const touch = event.touches?.[0];
      if (!touch) return;
      startX = touch.clientX;
      startY = touch.clientY;
      lastY = touch.clientY;
      axis = null;
    };

    const onTouchMove = (event) => {
      const touch = event.touches?.[0];
      if (!touch) return;

      if (!axis) {
        const dx = touch.clientX - startX;
        const dy = touch.clientY - startY;
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
      if (axis === 'x') return;

      const deltaY = lastY - touch.clientY;
      lastY = touch.clientY;
      if (!deltaY) return;

      const maxScroll = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
      const atTop = scroller.scrollTop <= 0;
      const atBottom = scroller.scrollTop >= maxScroll - 1;
      const pastEdge = (deltaY < 0 && atTop) || (deltaY > 0 && atBottom);
      if (!pastEdge) return;

      const scrolled = scrollSheetBy(scroller, deltaY);
      if (scrolled && event.cancelable) event.preventDefault();
    };

    scroller.addEventListener('touchstart', onTouchStart, { passive: true });
    scroller.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => {
      scroller.removeEventListener('touchstart', onTouchStart);
      scroller.removeEventListener('touchmove', onTouchMove);
    };
  }, []);

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const table = el.querySelector('table');
    if (table) ro.observe(table);
    const sheet = el.closest('.nettles-flow-sheet-scroll');
    if (sheet) ro.observe(sheet);
    if (footerRef.current) ro.observe(footerRef.current);
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
  }, [measure, measureKey]);

  const onTableScroll = (event) => {
    syncLinkedScrollLeft(event.currentTarget.scrollLeft);
  };

  const onFooterScroll = (event) => {
    syncLinkedScrollLeft(event.currentTarget.scrollLeft);
  };

  const onBarScroll = (event) => {
    syncLinkedScrollLeft(event.currentTarget.scrollLeft);
  };

  const onWheelCapture = (event) => {
    const scroller = event.currentTarget;
    const rect = scroller.getBoundingClientRect();
    const onFrozen = event.clientX - rect.left <= frozenWidth;
    const vertical = Math.abs(event.deltaY) > Math.abs(event.deltaX);
    if (!vertical) {
      if (onFrozen) {
        event.preventDefault();
        event.stopPropagation();
      }
      return;
    }

    const maxScroll = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
    const atTop = scroller.scrollTop <= 0;
    const atBottom = scroller.scrollTop >= maxScroll - 1;
    const pastEdge = (event.deltaY < 0 && atTop) || (event.deltaY > 0 && atBottom);

    if (pastEdge) {
      scrollSheetBy(scroller, event.deltaY);
      return;
    }

    if (onFrozen) {
      scroller.scrollTop += event.deltaY;
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const onTouchStartCapture = (event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    const rect = event.currentTarget.getBoundingClientRect();
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      frozen: touch.clientX - rect.left <= frozenWidth,
    };
  };

  const onTouchMoveCapture = (event) => {
    const start = touchStartRef.current;
    const touch = event.touches?.[0];
    if (!start?.frozen || !touch) return;
    const deltaX = Math.abs(touch.clientX - start.x);
    const deltaY = Math.abs(touch.clientY - start.y);
    if (deltaX <= deltaY) return;
    event.preventDefault();
    event.stopPropagation();
  };

  const showBar = hScroll.overflow > 1;
  const innerWidth = Math.max(hScroll.scrollWidth - frozenWidth, 0);

  return (
    <div className="relative w-full min-w-0 max-w-full overflow-hidden rounded-lg border border-gray-200">
      <div
        className={footer ? 'nettles-crew-table-hclip w-full min-w-0' : 'w-full min-w-0'}
        style={
          footer && maxHeight != null
            ? { maxHeight: `${maxHeight}px` }
            : undefined
        }
      >
      <div
        ref={scrollerRef}
        onScroll={onTableScroll}
        onWheelCapture={onWheelCapture}
        onTouchStartCapture={onTouchStartCapture}
        onTouchMoveCapture={onTouchMoveCapture}
        className={`nettles-crew-table-scroll w-full min-w-0 max-w-full overscroll-x-contain ${footer ? 'pb-0' : 'pb-3'}`}
        style={{
          maxHeight:
            maxHeight != null
              ? `${maxHeight + (footer ? 24 : 0)}px`
              : undefined,
          '--nettles-thead-row1': `${headRow1Height}px`,
          '--nettles-table-max-h': maxHeight != null ? `${maxHeight}px` : undefined,
        }}
      >
        {children}
      </div>
      </div>
      {footer ? (
        <div
          ref={footerRef}
          onScroll={onFooterScroll}
          className="nettles-crew-table-footer"
        >
          {footer}
        </div>
      ) : null}
      {showBar && (
        <div className="relative h-4 border-t border-gray-200 bg-stone-100">
          <div
            className="pointer-events-none absolute bottom-0 left-0 z-20 h-4 bg-stone-100"
            style={{ width: frozenWidth }}
            aria-hidden
          />
          <div
            ref={barRef}
            onScroll={onBarScroll}
            className="pointer-events-auto h-4 overflow-x-auto overflow-y-hidden [scrollbar-width:thin] [&::-webkit-scrollbar]:h-2"
            style={{ marginLeft: frozenWidth }}
          >
            <div style={{ height: '1px', width: `${innerWidth}px` }} />
          </div>
        </div>
      )}
    </div>
  );
}

const TEAM_LIST_CLIENT_TTL_MS = 10 * 60 * 1000;

function createTeamListCache(emptyMessage) {
  let entry = { at: 0, data: null, inflight: null };

  function peek() {
    return Array.isArray(entry.data) && entry.data.length > 0 ? entry.data : null;
  }

  function load(fetcher) {
    const cached = peek();
    if (cached && Date.now() - entry.at < TEAM_LIST_CLIENT_TTL_MS) {
      return Promise.resolve(cached);
    }
    if (entry.inflight) return entry.inflight;

    const previous = cached;
    entry.inflight = (async () => {
      try {
        const data = await fetcher();
        if (!Array.isArray(data) || data.length === 0) {
          const error = new Error(emptyMessage);
          error.isEmptyList = true;
          throw error;
        }
        entry = { at: Date.now(), data, inflight: null };
        return data;
      } catch (err) {
        entry.inflight = null;
        if (previous && previous.length > 0) return previous;
        throw err;
      }
    })();
    return entry.inflight;
  }

  return { peek, load };
}

const crewLeadsClientCache = createTeamListCache(
  "Crew leads are temporarily unavailable. Please try again."
);
const teamMembersClientCache = createTeamListCache(
  "Employee list is temporarily unavailable. Please try again."
);

function peekCrewLeads() {
  return crewLeadsClientCache.peek();
}

function fetchCrewLeads(getAuthConfig) {
  return crewLeadsClientCache.load(async () => {
    const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
    if (!baseUrl) throw new Error("Backend URL not configured.");
    const res = await axios.get(
      `${baseUrl}/api/miter/production-performance/team-members?activeOnly=true&requireCrew=true&crewLeadsOnly=true`,
      await getAuthConfig()
    );
    if (res.data?.status !== "success" || !Array.isArray(res.data?.data)) {
      throw new Error(res.data?.message || "Failed to load crew leads.");
    }
    return res.data.data;
  });
}

/**
 * Same order as the server: selected leads first, then their direct reports by name.
 * Uses the directory already on screen so the sheet can fill before the save returns.
 */
function peopleForSelectedCrews(leads, teamMembers) {
  const members = Array.isArray(teamMembers) ? teamMembers : [];
  const byId = new Map(
    members.filter((member) => member?.id).map((member) => [String(member.id), member])
  );
  const leadIds = (leads || [])
    .map((lead) => (lead?.id ? String(lead.id) : ''))
    .filter(Boolean);
  const leadIdSet = new Set(leadIds);
  const seen = new Set();
  const roster = [];
  const push = (person) => {
    const id = person?.id ? String(person.id) : '';
    if (!id || seen.has(id)) return;
    seen.add(id);
    roster.push(person);
  };
  leadIds.forEach((id) => {
    const fromDirectory = byId.get(id);
    const fromSelection = (leads || []).find((lead) => String(lead?.id) === id);
    if (fromDirectory || fromSelection) push(fromDirectory || fromSelection);
  });
  members
    .filter((member) => {
      const id = member?.id ? String(member.id) : '';
      const reportsTo = member?.reportsToId ? String(member.reportsToId) : '';
      return id && reportsTo && reportsTo !== id && leadIdSet.has(reportsTo);
    })
    .sort((a, b) =>
      String(a.name || '').localeCompare(String(b.name || ''), undefined, {
        sensitivity: 'base',
      })
    )
    .forEach(push);
  return roster;
}

function toSavedCrewEmployee(person, sortOrder) {
  return {
    id: String(person.id),
    name: person.name || 'Unknown',
    friendlyId: person.friendlyId || null,
    title: person.title || null,
    employmentStatus: person.employmentStatus || null,
    employmentType: person.employmentType || null,
    workLocation: person.workLocation || null,
    sortOrder,
    isDefault: false,
  };
}

function fetchTeamMembers(getAuthConfig) {
  return teamMembersClientCache.load(async () => {
    const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
    if (!baseUrl) throw new Error("Backend URL not configured.");
    const res = await axios.get(
      `${baseUrl}/api/miter/production-performance/team-members?activeOnly=true&requireCrew=true`,
      await getAuthConfig()
    );
    if (res.data?.status !== "success" || !Array.isArray(res.data?.data)) {
      throw new Error(res.data?.message || "Failed to load team members.");
    }
    return res.data.data;
  });
}

const ModalCrewTimesheet = ({
  isOpen,
  onClose,
  projectCode,
  projectName,
  rowData,
  projectActivities = [],
  onLogProduction = () => {},
  onLaborSaved = () => {},
  activityLoading = false,
  dailyLogStep = null,
  flowWorkDate = '',
  onFlowNavigate,
}) => {
  const { getAuthConfig, authFetch, isReady } = useAuthFetch();
  const [workDate, setWorkDate] = useState(getToday());
  const [activeRowData, setActiveRowData] = useState(rowData);

  const [jobLoading, setJobLoading] = useState(false);
  const [savedCrewEmployees, setSavedCrewEmployees] = useState([]);
  const [savedLaborTypes, setSavedLaborTypes] = useState([]);
  const [savedHoursByEmployee, setSavedHoursByEmployee] = useState({});
  const [savedHourLabelsByEmployee, setSavedHourLabelsByEmployee] = useState({});
  const [projectLaborColumns, setProjectLaborColumns] = useState([]);
  const [defaultLaborColumnDefs, setDefaultLaborColumnDefs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const [isAddLaborOpen, setIsAddLaborOpen] = useState(false);
  const [selectedLaborTypes, setSelectedLaborTypes] = useState([]);
  const [selectedLaborTypesToRemove, setSelectedLaborTypesToRemove] = useState([]);
  const [isUpdatingLaborTypes, setIsUpdatingLaborTypes] = useState(false);
  const [laborScopeCodesFromApi, setLaborScopeCodesFromApi] = useState([]);
  const [laborScopeCodesLoading, setLaborScopeCodesLoading] = useState(false);
  const [laborScopeCodesError, setLaborScopeCodesError] = useState(null);

  const [isAddEmployeeOpen, setIsAddEmployeeOpen] = useState(false);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [teamMembersFromApi, setTeamMembersFromApi] = useState([]);
  const [teamMembersLoading, setTeamMembersLoading] = useState(false);
  const [teamMembersError, setTeamMembersError] = useState(null);
  const [isAddingEmployees, setIsAddingEmployees] = useState(false);
  const [isAddCrewOpen, setIsAddCrewOpen] = useState(false);
  const [selectedCrewLeadIds, setSelectedCrewLeadIds] = useState([]);
  const [selectedCrewLeadIdsToRemove, setSelectedCrewLeadIdsToRemove] = useState([]);
  const [crewLeadsFromApi, setCrewLeadsFromApi] = useState(() => peekCrewLeads() || []);
  const [crewLeadsLoading, setCrewLeadsLoading] = useState(false);
  const [crewLeadsError, setCrewLeadsError] = useState(null);
  const [isUpdatingCrewLeads, setIsUpdatingCrewLeads] = useState(false);
  const [crewRemoveConfirm, setCrewRemoveConfirm] = useState({
    isOpen: false,
    crewsToAdd: [],
    crewsToRemove: [],
    employeeCount: 0,
  });
  const [crewLeadSearchQuery, setCrewLeadSearchQuery] = useState('');
  const [addCrewPopupPos, setAddCrewPopupPos] = useState(null);
  const [isCopyEmployeesOpen, setIsCopyEmployeesOpen] = useState(false);
  const [savedEmployeeDates, setSavedEmployeeDates] = useState([]);
  const [savedEmployeeDatesLoading, setSavedEmployeeDatesLoading] = useState(false);
  const [savedEmployeeDatesError, setSavedEmployeeDatesError] = useState(null);
  const [selectedCopyFromDate, setSelectedCopyFromDate] = useState('');
  const [isCopyingEmployees, setIsCopyingEmployees] = useState(false);
  const [addCopyEmployeesPopupPos, setAddCopyEmployeesPopupPos] = useState(null);
  const [deletingEmployeeId, setDeletingEmployeeId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    employees: [],
  });
  const [selectedTimesheetEmployeeIds, setSelectedTimesheetEmployeeIds] = useState(
    []
  );
  const [isSelectDeleteMode, setIsSelectDeleteMode] = useState(false);
  const [isUpdateMultipleOpen, setIsUpdateMultipleOpen] = useState(false);
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');
  const [addEmployeePopupPos, setAddEmployeePopupPos] = useState(null);
  const [addLaborPopupPos, setAddLaborPopupPos] = useState(null);
  const addCrewBtnRef = useRef(null);
  const addEmployeeBtnRef = useRef(null);
  const addLaborBtnRef = useRef(null);
  const copyEmployeesBtnRef = useRef(null);
  const selectAllCheckboxRef = useRef(null);

  // Travel Reimbursement — Branch + Miles From Branch from Dashboard project details.
  const [travelBranch, setTravelBranch] = useState('—');
  const [milesFromBranch, setMilesFromBranch] = useState('0');
  /** Miles keyed by Miter Work Location name (e.g. "Waller, TX"). */
  const [milesByWorkLocation, setMilesByWorkLocation] = useState({});
  const [travelMilesLoading, setTravelMilesLoading] = useState(false);
  const [travelByEmployeeId, setTravelByEmployeeId] = useState({});
  const [savedTravelByEmployee, setSavedTravelByEmployee] = useState({});
  const [isTravelUpdateMultipleOpen, setIsTravelUpdateMultipleOpen] =
    useState(false);
  const [isEquipmentTimeCardOpen, setIsEquipmentTimeCardOpen] = useState(false);

  const jobRequestSeqRef = useRef(0);
  const crewRosterRequestSeqRef = useRef(0);
  const crewLaborTypesRequestSeqRef = useRef(0);
  const crewHoursRequestSeqRef = useRef(0);
  const crewHoursRefreshOnceRef = useRef(false);
  const crewTravelRefreshCountRef = useRef(0);
  const travelMilesRequestSeqRef = useRef(0);
  const crewTravelRequestSeqRef = useRef(0);
  // Unsaved checkbox/miles edits. Background travel reload must not clear them.
  const travelEditedRef = useRef(false);

  useEffect(() => {
    if (isOpen) setActiveRowData(rowData);
  }, [isOpen, rowData]);

  const usingFlowNav = typeof onFlowNavigate === 'function';

  useEffect(() => {
    if (!isOpen || !usingFlowNav) return;
    if (dailyLogStep !== 'crew' && dailyLogStep !== 'equipment') return;
    if (!flowWorkDate || !/^\d{4}-\d{2}-\d{2}$/.test(flowWorkDate)) return;
    setWorkDate((current) => (current === flowWorkDate ? current : flowWorkDate));
  }, [isOpen, usingFlowNav, dailyLogStep, flowWorkDate]);

  // One shared body-scroll lock for every viewport. Nested Equipment skips this.
  useFullscreenSheetModal(isOpen);

  // Branch = Dashboard project Branch (company).
  // Miles From Branch = project company office → jobsite.
  // milesByWorkLocation = each known office → jobsite (for per-employee Work Location miles).
  // Fetch each office separately so row order cannot be swapped by multi-origin caching.
  useEffect(() => {
    if (!isOpen || !isReady) return;

    let cancelled = false;
    const requestSeq = ++travelMilesRequestSeqRef.current;

    const fetchOfficeMiles = async (officeAddress, destination) => {
      const res = await authFetch(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/distance/matrix`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            origins: [officeAddress],
            destinations: [destination],
            units: 'imperial',
          }),
        }
      );
      const data = await res.json();
      const distanceText =
        data?.success && data?.data?.rows?.[0]?.elements?.[0]?.distance?.text;
      return parseDistanceMiles(distanceText);
    };

    const loadBranchAndMiles = async () => {
      const projectData = getSelectedProjectDetails();
      const companyLabel = String(projectData.company || '').trim();
      const projectOffice = resolveTravelBranchOffice(companyLabel);

      setTravelBranch(projectOffice?.name || companyLabel || '—');
      setMilesFromBranch('0');
      setMilesByWorkLocation({});
      setTravelMilesLoading(true);

      const destination = buildProjectAddressText(projectData);
      if (!destination) {
        if (!cancelled && requestSeq === travelMilesRequestSeqRef.current) {
          setTravelMilesLoading(false);
        }
        return;
      }

      try {
        const results = await Promise.all(
          TRAVEL_BRANCH_OFFICES.map(async (office) => {
            try {
              const miles = await fetchOfficeMiles(office.address, destination);
              return { name: office.name, miles };
            } catch (err) {
              console.error(`Error fetching miles for ${office.name}:`, err);
              return { name: office.name, miles: null };
            }
          })
        );

        const nextByLocation = {};
        results.forEach(({ name, miles }) => {
          if (miles != null) nextByLocation[name] = miles;
        });

        const projectMiles =
          (projectOffice && nextByLocation[projectOffice.name]) ||
          '0';

        if (!cancelled && requestSeq === travelMilesRequestSeqRef.current) {
          setMilesByWorkLocation(nextByLocation);
          setMilesFromBranch(String(projectMiles || '0'));
          setTravelMilesLoading(false);
        }
      } catch (err) {
        console.error('Error fetching miles from branch:', err);
        if (!cancelled && requestSeq === travelMilesRequestSeqRef.current) {
          setTravelMilesLoading(false);
        }
      }
    };

    loadBranchAndMiles();
    return () => {
      cancelled = true;
    };
  }, [isOpen, isReady, authFetch]);

  const scopeName = activeRowData?.scope || activeRowData?.originalData?.scope || null;
  const scopeId = activeRowData?.scopeId || activeRowData?.originalData?.scopeId || null;
  const isMiterActivity = Boolean(scopeId) && !String(scopeId).startsWith('db-');

  const displayProjectName =
    projectName || activeRowData?.projectName || '—';
  const displayProjectCode = projectCode || '—';
  const displayActivity = activityLoading
    ? 'Loading…'
    : scopeName || '—';

  const fetchLaborColumns = useCallback(
    async () => {
      if (!isReady || !projectCode || !scopeName) {
        setDefaultLaborColumnDefs([]);
        setJobLoading(false);
        return;
      }
      const requestSeq = ++jobRequestSeqRef.current;
      setJobLoading(true);
      try {
        const base = process.env.REACT_APP_BACKEND_BASEURL;
        const params = new URLSearchParams({ scope: scopeName });
        const url = `${base}/api/miter/production-performance/${encodeURIComponent(
          projectCode
        )}/crew-timesheet-labor-columns?${params.toString()}`;
        const res = await axios.get(url, await getAuthConfig());
        if (requestSeq !== jobRequestSeqRef.current) return;
        const columns = res.data?.data?.columns;
        setDefaultLaborColumnDefs(Array.isArray(columns) ? columns : []);
      } catch {
        if (requestSeq === jobRequestSeqRef.current) setDefaultLaborColumnDefs([]);
      } finally {
        if (requestSeq === jobRequestSeqRef.current) setJobLoading(false);
      }
    },
    [isReady, projectCode, scopeName, getAuthConfig]
  );

  const fetchCrewEmployees = useCallback(async () => {
    if (!isReady || !projectCode || !scopeName || !/^\d{4}-\d{2}-\d{2}$/.test(workDate)) {
      setSavedCrewEmployees([]);
      return;
    }
    const requestSeq = ++crewRosterRequestSeqRef.current;
    try {
      const base = process.env.REACT_APP_BACKEND_BASEURL;
      const params = new URLSearchParams({ scope: scopeName });
      if (scopeId) params.set('scopeId', String(scopeId));
      const url = `${base}/api/miter/production-performance/${encodeURIComponent(
        projectCode
      )}/crew-timesheet-employees/${workDate}?${params.toString()}`;
      const res = await axios.get(url, await getAuthConfig());
      if (requestSeq !== crewRosterRequestSeqRef.current) return;
      if (res.data?.status === 'success' && Array.isArray(res.data?.data?.employees)) {
        setSavedCrewEmployees(res.data.data.employees);
      } else {
        setSavedCrewEmployees([]);
      }
    } catch {
      if (requestSeq === crewRosterRequestSeqRef.current) setSavedCrewEmployees([]);
    }
  }, [isReady, projectCode, workDate, scopeName, scopeId, getAuthConfig]);

  const fetchCrewLaborTypes = useCallback(async () => {
    if (!isReady || !projectCode || !scopeName || !/^\d{4}-\d{2}-\d{2}$/.test(workDate)) {
      setSavedLaborTypes([]);
      return;
    }
    const requestSeq = ++crewLaborTypesRequestSeqRef.current;
    try {
      const base = process.env.REACT_APP_BACKEND_BASEURL;
      const params = new URLSearchParams({ scope: scopeName });
      if (scopeId) params.set('scopeId', String(scopeId));
      const url = `${base}/api/miter/production-performance/${encodeURIComponent(
        projectCode
      )}/crew-timesheet-labor-types/${workDate}?${params.toString()}`;
      const res = await axios.get(url, await getAuthConfig());
      if (requestSeq !== crewLaborTypesRequestSeqRef.current) return;
      if (res.data?.status === 'success' && Array.isArray(res.data?.data?.laborTypes)) {
        setSavedLaborTypes(res.data.data.laborTypes);
      } else {
        setSavedLaborTypes([]);
      }
    } catch {
      if (requestSeq === crewLaborTypesRequestSeqRef.current) setSavedLaborTypes([]);
    }
  }, [isReady, projectCode, workDate, scopeName, scopeId, getAuthConfig]);

  const fetchCrewHours = useCallback(async () => {
    if (!isReady || !projectCode || !scopeName || !/^\d{4}-\d{2}-\d{2}$/.test(workDate)) {
      setSavedHoursByEmployee({});
      setSavedHourLabelsByEmployee({});
      return;
    }
    const requestSeq = ++crewHoursRequestSeqRef.current;
    try {
      const base = process.env.REACT_APP_BACKEND_BASEURL;
      const params = new URLSearchParams({ scope: scopeName });
      if (scopeId) params.set('scopeId', String(scopeId));
      const auth = await getAuthConfig();

      const hoursRes = await axios.get(
        `${base}/api/miter/production-performance/${encodeURIComponent(
          projectCode
        )}/crew-timesheet-hours/${workDate}?${params.toString()}`,
        auth
      );

      if (requestSeq !== crewHoursRequestSeqRef.current) return;

      const hoursMap = {};
      const labelsMap = {};
      const hourRows = hoursRes.data?.data?.hoursGrid;
      if (hoursRes.data?.status === 'success' && Array.isArray(hourRows)) {
        hourRows.forEach((row) => {
          if (!row?.employeeId) return;
          const empId = String(row.employeeId);
          hoursMap[empId] = row.hours || {};
          labelsMap[empId] = row.laborLabels || {};
        });
      }
      setSavedHoursByEmployee(hoursMap);
      setSavedHourLabelsByEmployee(labelsMap);
      if (hoursRes.data?.data?.refresh && !crewHoursRefreshOnceRef.current) {
        crewHoursRefreshOnceRef.current = true;
        window.setTimeout(() => {
          if (requestSeq === crewHoursRequestSeqRef.current) fetchCrewHours();
        }, 2500);
      }
    } catch {
      // Keep the hours already on screen. Clearing them made a slow or
      // rate-limited load look like the saved row had disappeared.
    }
  }, [isReady, projectCode, workDate, scopeName, scopeId, getAuthConfig]);

  const fetchCrewTravel = useCallback(async () => {
    if (!isReady || !projectCode || !scopeName || !/^\d{4}-\d{2}-\d{2}$/.test(workDate)) {
      setSavedTravelByEmployee({});
      return;
    }
    const requestSeq = ++crewTravelRequestSeqRef.current;
    try {
      const base = process.env.REACT_APP_BACKEND_BASEURL;
      const params = new URLSearchParams({ scope: scopeName });
      if (scopeId) params.set('scopeId', String(scopeId));
      const res = await axios.get(
        `${base}/api/miter/production-performance/${encodeURIComponent(
          projectCode
        )}/crew-timesheet-travel/${workDate}?${params.toString()}`,
        await getAuthConfig()
      );
      if (requestSeq !== crewTravelRequestSeqRef.current) return;

      const travelMap = {};
      const rows = res.data?.data?.travelGrid;
      if (res.data?.status === 'success' && Array.isArray(rows)) {
        rows.forEach((row) => {
          if (!row?.employeeId) return;
          travelMap[String(row.employeeId)] = row;
        });
        setSavedTravelByEmployee(travelMap);
      }
      // Local flags return first. Miter reconcile can take longer than one
      // refresh for a full crew, so poll a couple more times to pick up
      // checkboxes (e.g. Employee Paid) once ids are linked.
      // Stop polling once the user starts editing — a later payload is the
      // last save and would wipe the boxes they just checked.
      if (
        res.data?.data?.refresh &&
        !travelEditedRef.current &&
        crewTravelRefreshCountRef.current < 2
      ) {
        crewTravelRefreshCountRef.current += 1;
        const delay = crewTravelRefreshCountRef.current === 1 ? 8000 : 16000;
        window.setTimeout(() => {
          if (requestSeq === crewTravelRequestSeqRef.current) fetchCrewTravel();
        }, delay);
      }
    } catch {
      // Keep checkboxes already on screen. Clearing them made a slow Miter
      // reconcile look like travel was never saved.
    }
  }, [isReady, projectCode, workDate, scopeName, scopeId, getAuthConfig]);

  useEffect(() => {
    if (!isOpen || !projectCode || !isReady) {
      setSavedCrewEmployees([]);
      setSavedLaborTypes([]);
      setSavedHoursByEmployee({});
      setSavedHourLabelsByEmployee({});
      setSavedTravelByEmployee({});
      setTravelByEmployeeId({});
      setDefaultLaborColumnDefs([]);
      setProjectLaborColumns([]);
      setEmployees([]);
      setMessage(null);
      setIsUpdateMultipleOpen(false);
      setIsTravelUpdateMultipleOpen(false);
      setIsEquipmentTimeCardOpen(false);
      setSelectedTimesheetEmployeeIds([]);
      setIsSelectDeleteMode(false);
      setSelectedEmployeeIds([]);
      setDeleteConfirm({ isOpen: false, employees: [] });
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(workDate)) return;

    if (activityLoading && !scopeName) {
      return;
    }

    setSavedCrewEmployees([]);
    setSavedLaborTypes([]);
    crewHoursRefreshOnceRef.current = false;
    crewTravelRefreshCountRef.current = 0;
    travelEditedRef.current = false;
    setSavedHoursByEmployee({});
    setSavedHourLabelsByEmployee({});
    setSavedTravelByEmployee({});
    setTravelByEmployeeId({});
    setEmployees([]);
    setMessage(null);
    setSelectedTimesheetEmployeeIds([]);
    setIsSelectDeleteMode(false);
    setDeleteConfirm({ isOpen: false, employees: [] });

    fetchLaborColumns();
    fetchCrewEmployees();
    fetchCrewLaborTypes();
    fetchCrewHours();
    fetchCrewTravel();
  }, [
    isOpen,
    projectCode,
    workDate,
    scopeName,
    scopeId,
    isReady,
    fetchLaborColumns,
    fetchCrewEmployees,
    fetchCrewLaborTypes,
    fetchCrewHours,
    fetchCrewTravel,
    activityLoading,
  ]);

  useEffect(() => {
    if (!isOpen || !isReady) return;
    const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
    if (!baseUrl) {
      setLaborScopeCodesError('Backend URL not configured.');
      return;
    }
    setLaborScopeCodesLoading(true);
    setLaborScopeCodesError(null);
    const qs = projectCode ? `?projectCode=${encodeURIComponent(projectCode)}` : '';
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${baseUrl}/api/miter/production-performance/labor-scope-codes${qs}`,
          await getAuthConfig()
        );
        if (cancelled) return;
        if (res.data?.status === 'success' && Array.isArray(res.data?.data)) {
          setLaborScopeCodesFromApi(res.data.data);
        } else {
          setLaborScopeCodesFromApi([]);
        }
      } catch (err) {
        if (cancelled) return;
        setLaborScopeCodesFromApi([]);
        setLaborScopeCodesError(
          err.response?.data?.message || err.message || 'Failed to load labor types.'
        );
      } finally {
        if (!cancelled) setLaborScopeCodesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, projectCode, isReady, getAuthConfig]);

  useEffect(() => {
    if (!isOpen) return;
    const defaults = buildDefaultLaborColumnsFromDescriptions(defaultLaborColumnDefs);
    const defaultLabels = new Set(defaults.map((c) => c.label.toLowerCase()));
    const extras = (savedLaborTypes || [])
      .map((t) => {
        const label = String(t.label || '').trim();
        if (!label || defaultLabels.has(label.toLowerCase())) return null;
        const code = String(t.descriptionCode || '').trim();
        return {
          id: buildLaborColumnId({
            label,
            descriptionCode: code,
            prefix: 'extra',
          }),
          label,
          descriptionCode: code || null,
          isDefault: false,
        };
      })
      .filter(Boolean);
    setProjectLaborColumns([...defaults, ...extras]);
  }, [isOpen, scopeName, workDate, defaultLaborColumnDefs, savedLaborTypes]);

  useEffect(() => {
    if (!isOpen) return;
    setEmployees((prev) =>
      buildEmployeesFromRoster(
        projectLaborColumns,
        prev,
        savedCrewEmployees,
        savedHoursByEmployee,
        savedHourLabelsByEmployee
      )
    );
  }, [
    isOpen,
    savedCrewEmployees,
    projectLaborColumns,
    savedHoursByEmployee,
    savedHourLabelsByEmployee,
  ]);

  // When persisted hours arrive/refresh, rehydrate from saved grid (no Miter single-column dump).
  useEffect(() => {
    if (!isOpen) return;
    setEmployees(
      buildEmployeesFromRoster(
        projectLaborColumns,
        [],
        savedCrewEmployees,
        savedHoursByEmployee,
        savedHourLabelsByEmployee
      )
    );
  }, [isOpen, savedHoursByEmployee, savedHourLabelsByEmployee]);

  useEffect(() => {
    if (!isAddLaborOpen) return;
    setSelectedLaborTypes([]);
    setSelectedLaborTypesToRemove([]);
  }, [isAddLaborOpen]);

  // Auto-clear feedback messages — same as Enter Data (success 5s, error 15s).
  useEffect(() => {
    if (!message) return undefined;
    const delayMs = message.type === 'error' ? 15000 : 5000;
    const timeoutId = window.setTimeout(() => {
      setMessage(null);
    }, delayMs);
    return () => window.clearTimeout(timeoutId);
  }, [message]);

  // Clamp popovers to the viewport (phones / PWA can otherwise overflow).
  const clampPopupPos = (rect, preferredWidth) => {
    const margin = 8;
    const width = Math.min(
      Math.max(rect.width, preferredWidth),
      Math.max(margin * 2, window.innerWidth - margin * 2)
    );
    const left = Math.min(
      Math.max(margin, rect.left),
      Math.max(margin, window.innerWidth - width - margin)
    );
    return {
      left,
      bottom: Math.max(margin, window.innerHeight - rect.top + 8),
      width,
    };
  };

  // Same as Enter Data: open dropdowns above the button so they aren't clipped by the footer.
  useEffect(() => {
    if (!isAddCrewOpen) {
      setAddCrewPopupPos(null);
      return undefined;
    }
    const updatePos = () => {
      const el = addCrewBtnRef.current;
      if (!el) return;
      setAddCrewPopupPos(clampPopupPos(el.getBoundingClientRect(), 320));
    };
    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [isAddCrewOpen]);

  useEffect(() => {
    if (!isAddEmployeeOpen) {
      setAddEmployeePopupPos(null);
      return undefined;
    }
    const updatePos = () => {
      const el = addEmployeeBtnRef.current;
      if (!el) return;
      setAddEmployeePopupPos(clampPopupPos(el.getBoundingClientRect(), 320));
    };
    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [isAddEmployeeOpen]);

  useEffect(() => {
    if (!isAddLaborOpen) {
      setAddLaborPopupPos(null);
      return undefined;
    }
    const updatePos = () => {
      const el = addLaborBtnRef.current;
      if (!el) return;
      setAddLaborPopupPos(clampPopupPos(el.getBoundingClientRect(), 320));
    };
    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [isAddLaborOpen]);

  // Team members for Add Employee dropdown (not needed for Travel Work Location —
  // roster GET already includes workLocation for immediate display).
  useEffect(() => {
    if (!isAddEmployeeOpen || !isReady) return;
    setSelectedEmployeeIds([]);
    setEmployeeSearchQuery('');
    setTeamMembersError(null);
    const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
    if (!baseUrl) {
      setTeamMembersError('Backend URL not configured.');
      return;
    }
    const cachedMembers = teamMembersClientCache.peek();
    if (cachedMembers) {
      setTeamMembersFromApi(cachedMembers);
      setTeamMembersLoading(false);
    } else {
      setTeamMembersLoading(true);
    }
    let cancelled = false;
    fetchTeamMembers(getAuthConfig)
      .then((data) => {
        if (cancelled || !Array.isArray(data) || data.length === 0) return;
        setTeamMembersFromApi(data);
        setTeamMembersError(null);
      })
      .catch((err) => {
        if (cancelled || teamMembersClientCache.peek()) return;
        setTeamMembersFromApi([]);
        setTeamMembersError(
          err.response?.data?.message ||
            err.message ||
            'Failed to load team members.'
        );
      })
      .finally(() => {
        if (!cancelled) setTeamMembersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAddEmployeeOpen, isReady, getAuthConfig]);

  useEffect(() => {
    if (!isOpen || !isReady) return undefined;
    let cancelled = false;
    const cached = peekCrewLeads();
    if (cached) setCrewLeadsFromApi(cached);
    fetchCrewLeads(getAuthConfig)
      .then((data) => {
        if (!cancelled && Array.isArray(data) && data.length > 0) setCrewLeadsFromApi(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isOpen, isReady, getAuthConfig]);

  // Crew membership (lead + direct reports) is needed to disable a lead whose
  // whole crew is already on the sheet. Same directory Add Employee uses.
  useEffect(() => {
    if (!isOpen || !isReady) return undefined;
    const cached = teamMembersClientCache.peek();
    if (cached) setTeamMembersFromApi(cached);
    let cancelled = false;
    fetchTeamMembers(getAuthConfig)
      .then((data) => {
        if (!cancelled && Array.isArray(data) && data.length > 0) {
          setTeamMembersFromApi(data);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isOpen, isReady, getAuthConfig]);

  useEffect(() => {
    if (!isAddCrewOpen || !isReady) return undefined;
    setSelectedCrewLeadIds([]);
    setSelectedCrewLeadIdsToRemove([]);
    setCrewLeadSearchQuery('');
    setCrewLeadsError(null);
    const cached = peekCrewLeads();
    if (cached) {
      setCrewLeadsFromApi(cached);
      setCrewLeadsLoading(false);
    } else {
      setCrewLeadsLoading(true);
    }
    let cancelled = false;
    fetchCrewLeads(getAuthConfig)
      .then((data) => {
        if (cancelled || !Array.isArray(data) || data.length === 0) return;
        setCrewLeadsFromApi(data);
        setCrewLeadsError(null);
      })
      .catch((err) => {
        if (cancelled || peekCrewLeads()) return;
        setCrewLeadsFromApi([]);
        setCrewLeadsError(
          err.response?.data?.message || err.message || 'Failed to load crew leads.'
        );
      })
      .finally(() => {
        if (!cancelled) setCrewLeadsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAddCrewOpen, isReady, getAuthConfig]);

  useEffect(() => {
    if (!isCopyEmployeesOpen) {
      setAddCopyEmployeesPopupPos(null);
      return undefined;
    }
    const updatePos = () => {
      const el = copyEmployeesBtnRef.current;
      if (!el) return;
      setAddCopyEmployeesPopupPos(clampPopupPos(el.getBoundingClientRect(), 300));
    };
    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [isCopyEmployeesOpen]);

  useEffect(() => {
    if (!isCopyEmployeesOpen || !isReady) return;
    setSelectedCopyFromDate('');
    setSavedEmployeeDatesError(null);
    if (!projectCode || !scopeName) {
      setSavedEmployeeDates([]);
      setSavedEmployeeDatesError('Project and activity are required.');
      return undefined;
    }
    const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
    if (!baseUrl) {
      setSavedEmployeeDatesError('Backend URL not configured.');
      return undefined;
    }
    setSavedEmployeeDatesLoading(true);
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({ scope: scopeName });
        if (workDate) params.set('excludeWorkDate', workDate);
        const res = await axios.get(
          `${baseUrl}/api/miter/production-performance/${encodeURIComponent(
            projectCode
          )}/crew-timesheet-employee-dates?${params.toString()}`,
          await getAuthConfig()
        );
        if (cancelled) return;
        if (res.data?.status === 'success' && Array.isArray(res.data?.data?.dates)) {
          setSavedEmployeeDates(res.data.data.dates);
        } else {
          setSavedEmployeeDates([]);
        }
      } catch (err) {
        if (cancelled) return;
        setSavedEmployeeDates([]);
        setSavedEmployeeDatesError(
          err.response?.data?.message ||
            err.message ||
            'Failed to load saved employee dates.'
        );
      } finally {
        if (!cancelled) setSavedEmployeeDatesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isCopyEmployeesOpen, isReady, projectCode, scopeName, workDate, getAuthConfig]);

  const existingLaborLabels = useMemo(
    () => projectLaborColumns.map((c) => String(c.label || '').trim()),
    [projectLaborColumns]
  );

  const scopeEntry = findScopeEntry(laborScopeCodesFromApi, scopeName);

  const availableDescriptionOptions = scopeEntry
    ? (scopeEntry.scope_descriptions || [])
        .filter((d) => {
          const name = String(d.description_name || '').trim().toLowerCase();
          if (!name) return false;
          return !existingLaborLabels.some((l) => l.toLowerCase() === name);
        })
        .map((d) => ({
          id: `${scopeEntry.scope_code}-${d.description_code}`,
          label: d.description_name,
          descriptionCode: d.description_code || null,
          scopeCode: scopeEntry.scope_code || null,
        }))
    : [];

  const selectedDescriptionRows = availableDescriptionOptions
    .filter((opt) => (selectedLaborTypes || []).includes(opt.id))
    .map((opt) => ({
      descriptionName: opt.label,
      descriptionCode: opt.descriptionCode || null,
      scopeCode: opt.scopeCode || null,
    }));

  /** Columns added with Add Labor Type — default activity columns stay fixed. */
  const removableLaborColumns = useMemo(
    () => (projectLaborColumns || []).filter((col) => col && col.isDefault === false),
    [projectLaborColumns]
  );

  const selectedRemovableLaborColumns = removableLaborColumns.filter((col) =>
    (selectedLaborTypesToRemove || []).includes(col.id)
  );

  const existingEmployeeIds = useMemo(
    () => new Set(employees.map((e) => String(e.id))),
    [employees]
  );

  /** Miter Employment → Location, keyed by team member id (roster first, then Add Employee cache). */
  const workLocationByEmployeeId = useMemo(() => {
    const map = {};
    (employees || []).forEach((emp) => {
      if (!emp?.id) return;
      const loc = String(emp.workLocation || '').trim();
      if (loc) map[String(emp.id)] = loc;
    });
    (teamMembersFromApi || []).forEach((m) => {
      if (!m?.id) return;
      const id = String(m.id);
      if (map[id]) return;
      const loc = String(m.workLocation || '').trim();
      if (loc) map[id] = loc;
    });
    return map;
  }, [employees, teamMembersFromApi]);

  const getEmployeeDefaultMiles = useCallback(
    (employeeId) => {
      const loc = workLocationByEmployeeId[String(employeeId)] || '';
      return getDefaultMilesForWorkLocation(
        loc,
        milesByWorkLocation,
        milesFromBranch
      );
    },
    [workLocationByEmployeeId, milesByWorkLocation, milesFromBranch]
  );

  const availableEmployeeOptions = useMemo(
    () =>
      (teamMembersFromApi || []).filter(
        (m) => m?.id && !existingEmployeeIds.has(String(m.id))
      ),
    [teamMembersFromApi, existingEmployeeIds]
  );

  const filteredEmployeeOptions = useMemo(() => {
    const q = String(employeeSearchQuery || '').trim().toLowerCase();
    if (!q) return availableEmployeeOptions;
    return availableEmployeeOptions.filter((m) => {
      const name = String(m.name || '').toLowerCase();
      const friendlyId = String(m.friendlyId || '').toLowerCase();
      const id = String(m.id || '').toLowerCase();
      return name.includes(q) || friendlyId.includes(q) || id.includes(q);
    });
  }, [availableEmployeeOptions, employeeSearchQuery]);

  const selectedEmployeesToAdd = availableEmployeeOptions.filter((m) =>
    (selectedEmployeeIds || []).includes(String(m.id))
  );

  // Direct reports of each lead (same rule as Add Crew: active members with a crew).
  const reportIdsByLeadId = useMemo(() => {
    const map = new Map();
    (teamMembersFromApi || []).forEach((member) => {
      const id = member?.id ? String(member.id) : '';
      const reportsTo = member?.reportsToId ? String(member.reportsToId) : '';
      if (!id || !reportsTo || reportsTo === id) return;
      if (!map.has(reportsTo)) map.set(reportsTo, []);
      map.get(reportsTo).push(id);
    });
    return map;
  }, [teamMembersFromApi]);

  // Require reportsToId on the directory. An empty or stripped list must not
  // treat a lead as fully added just because that lead is already on the sheet.
  const crewRosterReady = useMemo(
    () =>
      (teamMembersFromApi || []).some(
        (member) => member?.id && member?.reportsToId
      ),
    [teamMembersFromApi]
  );

  // A lead is fully on the sheet only when the lead and every direct report are listed.
  // Fully-added leads move to the Remove list (same pattern as Add/Remove Labor Type).
  const fullyAddedCrewLeadIds = useMemo(() => {
    const set = new Set();
    if (!crewRosterReady) return set;
    (crewLeadsFromApi || []).forEach((lead) => {
      const id = lead?.id ? String(lead.id) : '';
      if (!id || !existingEmployeeIds.has(id)) return;
      const reports = reportIdsByLeadId.get(id) || [];
      if (reports.every((reportId) => existingEmployeeIds.has(reportId))) {
        set.add(id);
      }
    });
    return set;
  }, [crewRosterReady, crewLeadsFromApi, existingEmployeeIds, reportIdsByLeadId]);

  const filterCrewLeadsBySearch = useCallback(
    (leads) => {
      const q = String(crewLeadSearchQuery || '').trim().toLowerCase();
      if (!q) return leads;
      return leads.filter((m) => {
        const name = String(m.name || '').toLowerCase();
        const friendlyId = String(m.friendlyId || '').toLowerCase();
        const id = String(m.id || '').toLowerCase();
        return name.includes(q) || friendlyId.includes(q) || id.includes(q);
      });
    },
    [crewLeadSearchQuery]
  );

  // Add list — crew leads not fully on the sheet yet.
  const availableCrewLeadOptions = useMemo(
    () =>
      (crewLeadsFromApi || []).filter(
        (m) => m?.id && !fullyAddedCrewLeadIds.has(String(m.id))
      ),
    [crewLeadsFromApi, fullyAddedCrewLeadIds]
  );

  const filteredCrewLeadOptions = useMemo(
    () => filterCrewLeadsBySearch(availableCrewLeadOptions),
    [availableCrewLeadOptions, filterCrewLeadsBySearch]
  );

  // Remove list — crew leads already fully on the sheet.
  const removableCrewLeadOptions = useMemo(
    () =>
      (crewLeadsFromApi || []).filter(
        (m) => m?.id && fullyAddedCrewLeadIds.has(String(m.id))
      ),
    [crewLeadsFromApi, fullyAddedCrewLeadIds]
  );

  const filteredRemovableCrewLeadOptions = useMemo(
    () => filterCrewLeadsBySearch(removableCrewLeadOptions),
    [removableCrewLeadOptions, filterCrewLeadsBySearch]
  );

  const selectedCrewLeadsToAdd = availableCrewLeadOptions.filter((m) =>
    (selectedCrewLeadIds || []).includes(String(m.id))
  );

  const selectedCrewLeadsToRemove = removableCrewLeadOptions.filter((m) =>
    (selectedCrewLeadIdsToRemove || []).includes(String(m.id))
  );

  useEffect(() => {
    if (fullyAddedCrewLeadIds.size === 0) return;
    setSelectedCrewLeadIds((prev) => {
      const next = prev.filter((id) => !fullyAddedCrewLeadIds.has(String(id)));
      return next.length === prev.length ? prev : next;
    });
  }, [fullyAddedCrewLeadIds]);

  useEffect(() => {
    setSelectedCrewLeadIdsToRemove((prev) => {
      const next = prev.filter((id) => fullyAddedCrewLeadIds.has(String(id)));
      return next.length === prev.length ? prev : next;
    });
  }, [fullyAddedCrewLeadIds]);

  const allHourColumns = useMemo(
    () => [...projectLaborColumns, ...NON_PROJECT_TYPES],
    [projectLaborColumns]
  );

  const columnTotals = useMemo(() => {
    const totals = {};
    allHourColumns.forEach((col) => {
      totals[col.id] = round2(
        employees.reduce((sum, emp) => sum + toNumber(emp.hours?.[col.id]), 0)
      );
    });
    return totals;
  }, [employees, allHourColumns]);

  const getLaborRowTotal = (emp) =>
    round2(
      allHourColumns.reduce((sum, col) => sum + toNumber(emp.hours?.[col.id]), 0)
    );

  const laborGrandTotal = useMemo(
    () =>
      round2(
        employees.reduce(
          (sum, emp) =>
            sum +
            allHourColumns.reduce(
              (rowSum, col) => rowSum + toNumber(emp.hours?.[col.id]),
              0
            ),
          0
        )
      ),
    [employees, allHourColumns]
  );

  /**
   * Project Labor hours only (exclude Non-Project columns like Warehouse / Equip. Repair).
   * This is what Save and Log Production pushes to Miter for the current job + activity.
   * Example: Project Labor 1+2+1+1+1=6 → Miter gets 6 (not row Total 9).
   */
  const getProjectLaborHours = (emp) =>
    round2(
      projectLaborColumns.reduce((sum, col) => {
        if (NON_PROJECT_COLUMN_IDS.has(col.id)) return sum;
        return sum + toNumber(emp.hours?.[col.id]);
      }, 0)
    );

  const handleHoursChange = (employeeId, columnId, value) => {
    if (!isValidPartialHoursInput(value)) return;
    setEmployees((prev) =>
      prev.map((emp) =>
        emp.id === employeeId
          ? { ...emp, hours: { ...emp.hours, [columnId]: value } }
          : emp
      )
    );
  };

  // Keep Travel Reimbursement rows in sync with the Crew Time Sheet employee list.
  useEffect(() => {
    setTravelByEmployeeId((prev) => {
      const next = {};
      let changed = false;
      employees.forEach((emp) => {
        const id = String(emp.id);
        const defaultMiles = getEmployeeDefaultMiles(id);
        if (prev[id]) {
          next[id] = prev[id];
        } else {
          const saved = savedTravelByEmployee[id];
          next[id] = saved
            ? normalizeTravelEntry(saved, defaultMiles)
            : buildEmptyTravelEntry(defaultMiles);
          if (next[id].overrideDefaultMiles === false) {
            next[id] = withTravelAmount({
              ...next[id],
              miles: String(defaultMiles),
            });
          }
          changed = true;
        }
      });
      if (Object.keys(prev).length !== Object.keys(next).length) changed = true;
      return changed ? next : prev;
    });
  }, [employees, getEmployeeDefaultMiles, savedTravelByEmployee]);

  // Rehydrate travel rows when saved travel arrives/refreshes (modal open / after save).
  useEffect(() => {
    const ids = Object.keys(savedTravelByEmployee || {});
    if (!ids.length) return;
    setTravelByEmployeeId((prev) => {
      const next = { ...prev };
      let changed = false;
      ids.forEach((id) => {
        const defaultMiles = getEmployeeDefaultMiles(id);
        const hydrated = normalizeTravelEntry(
          savedTravelByEmployee[id],
          defaultMiles
        );
        const applied = hydrated.overrideDefaultMiles
          ? hydrated
          : withTravelAmount({
              ...hydrated,
              miles: String(defaultMiles),
            });
        const prevRow = prev[id];
        // User has checked or unchecked since the last save. Keep those flags.
        // Only attach Miter ids that arrived from the background sync.
        if (travelEditedRef.current && prevRow) {
          const mergedIds = {
            ...(prevRow.miterIds || {}),
            ...(applied.miterIds || {}),
          };
          if (
            JSON.stringify(prevRow.miterIds || {}) !== JSON.stringify(mergedIds)
          ) {
            next[id] = { ...prevRow, miterIds: mergedIds };
            changed = true;
          }
          return;
        }
        if (
          !prevRow ||
          prevRow.overrideDefaultMiles !== applied.overrideDefaultMiles ||
          String(prevRow.miles) !== String(applied.miles) ||
          prevRow.amount !== applied.amount ||
          JSON.stringify(prevRow.flags || {}) !==
            JSON.stringify(applied.flags || {}) ||
          JSON.stringify(prevRow.miterIds || {}) !==
            JSON.stringify(applied.miterIds || {})
        ) {
          next[id] = applied;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    // Intentionally only when saved payload / default miles map changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedTravelByEmployee, getEmployeeDefaultMiles]);

  // When per-employee default miles change, push into rows that are not overriding.
  useEffect(() => {
    setTravelByEmployeeId((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.keys(next).forEach((id) => {
        const row = next[id];
        const defaultMiles = getEmployeeDefaultMiles(id);
        if (!row?.overrideDefaultMiles && String(row.miles) !== String(defaultMiles)) {
          next[id] = withTravelAmount({ ...row, miles: String(defaultMiles) });
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [getEmployeeDefaultMiles]);

  const handleTravelOverrideChange = (employeeId, checked) => {
    travelEditedRef.current = true;
    const id = String(employeeId);
    const defaultMiles = getEmployeeDefaultMiles(id);
    setTravelByEmployeeId((prev) => {
      const row = prev[id] || buildEmptyTravelEntry(defaultMiles);
      return {
        ...prev,
        [id]: withTravelAmount({
          ...row,
          overrideDefaultMiles: checked,
          miles: checked ? row.miles : String(defaultMiles),
        }),
      };
    });
  };

  const handleTravelMilesChange = (employeeId, value) => {
    if (!isValidPartialHoursInput(value)) return;
    travelEditedRef.current = true;
    const id = String(employeeId);
    const defaultMiles = getEmployeeDefaultMiles(id);
    setTravelByEmployeeId((prev) => {
      const row = prev[id] || buildEmptyTravelEntry(defaultMiles);
      if (!row.overrideDefaultMiles) return prev;
      return {
        ...prev,
        [id]: withTravelAmount({ ...row, miles: value }),
      };
    });
  };

  const handleTravelFlagChange = (employeeId, flagId, checked) => {
    travelEditedRef.current = true;
    const id = String(employeeId);
    const defaultMiles = getEmployeeDefaultMiles(id);
    setTravelByEmployeeId((prev) => {
      const row = prev[id] || buildEmptyTravelEntry(defaultMiles);
      return {
        ...prev,
        [id]: withTravelAmount({
          ...row,
          flags: { ...(row.flags || {}), [flagId]: checked },
        }),
      };
    });
  };

  const handleTravelBulkUpdate = ({
    employeeIds = [],
    flagsPatch = null,
    milesValue = null,
    setOverride = null,
  }) => {
    const ids = (employeeIds || []).map(String).filter(Boolean);
    if (!ids.length) return;
    travelEditedRef.current = true;
    setTravelByEmployeeId((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        const defaultMiles = getEmployeeDefaultMiles(id);
        const row = next[id] || buildEmptyTravelEntry(defaultMiles);
        let updated = { ...row, flags: { ...(row.flags || {}) } };
        if (flagsPatch && typeof flagsPatch === 'object') {
          Object.entries(flagsPatch).forEach(([flagId, value]) => {
            if (value == null) return;
            updated.flags[flagId] = Boolean(value);
          });
        }
        if (setOverride === true) {
          updated.overrideDefaultMiles = true;
          if (milesValue != null && milesValue !== '') {
            updated.miles = String(milesValue);
          }
        } else if (setOverride === false) {
          updated.overrideDefaultMiles = false;
          updated.miles = String(defaultMiles);
        } else if (
          updated.overrideDefaultMiles &&
          milesValue != null &&
          milesValue !== ''
        ) {
          updated.miles = String(milesValue);
        }
        next[id] = withTravelAmount(updated);
      });
      return next;
    });
  };

  const handleUpdateMultipleHours = ({ employeeIds, hoursByColumnId }) => {
    const selected = new Set((employeeIds || []).map((id) => String(id)));
    const hoursMap = hoursByColumnId || {};
    setEmployees((prev) =>
      prev.map((emp) => {
        if (!selected.has(String(emp.id))) return emp;
        const nextHours = { ...(emp.hours || {}) };
        Object.entries(hoursMap).forEach(([columnId, hours]) => {
          nextHours[columnId] = hours;
        });
        return { ...emp, hours: nextHours };
      })
    );
    setMessage({
      type: 'success',
      text:
        (employeeIds || []).length === 1
          ? 'Hours updated for 1 employee.'
          : `Hours updated for ${(employeeIds || []).length} employees.`,
    });
  };

  const handleLaborTypesUpdate = async () => {
    const existingSet = new Set(existingLaborLabels.map((d) => d.toLowerCase()));
    const typesToAdd = selectedDescriptionRows
      .map((row) => ({
        label: String(row?.descriptionName || '').trim(),
        descriptionCode: row?.descriptionCode || null,
        scopeCode: row?.scopeCode || null,
      }))
      .filter((t) => t.label && !existingSet.has(t.label.toLowerCase()));
    const typesToRemove = selectedRemovableLaborColumns;

    if (typesToAdd.length === 0 && typesToRemove.length === 0) {
      setIsAddLaborOpen(false);
      return;
    }
    if (!projectCode || !workDate || !scopeName) {
      setMessage({
        type: 'error',
        text: 'Project, work date, and activity are required to update labor types.',
      });
      return;
    }

    const removedIds = new Set(typesToRemove.map((col) => String(col.id)));
    const removedLabels = new Set(
      typesToRemove.map((col) => String(col.label || '').trim().toLowerCase())
    );
    const hadHours = employees.some((emp) =>
      typesToRemove.some((col) => toNumber(emp.hours?.[col.id]) > 0)
    );

    setIsUpdatingLaborTypes(true);
    setMessage(null);
    try {
      const base = process.env.REACT_APP_BACKEND_BASEURL;
      const auth = await getAuthConfig();

      if (typesToRemove.length > 0) {
        const res = await axios.post(
          `${base}/api/miter/production-performance/${encodeURIComponent(
            projectCode
          )}/crew-timesheet-labor-types/remove`,
          {
            workDate,
            scope: scopeName,
            scopeId: scopeId ?? null,
            laborTypes: typesToRemove.map((col) => ({
              label: col.label,
              descriptionCode: col.descriptionCode || null,
              columnId: col.id,
            })),
          },
          auth
        );

        if (res.data?.status === 'success' && Array.isArray(res.data?.data?.laborTypes)) {
          crewLaborTypesRequestSeqRef.current += 1;
          setSavedLaborTypes(res.data.data.laborTypes);
        } else {
          await fetchCrewLaborTypes();
        }

        const dropRemovedHours = (hours, labelsByKey = {}) => {
          if (!hours || typeof hours !== 'object') return hours;
          const next = { ...hours };
          let changed = false;
          Object.keys(next).forEach((key) => {
            const label = String(labelsByKey[key] || '')
              .trim()
              .toLowerCase();
            const keyLabel = String(key || '').trim().toLowerCase();
            if (
              removedIds.has(String(key)) ||
              (keyLabel && removedLabels.has(keyLabel)) ||
              (label && removedLabels.has(label))
            ) {
              delete next[key];
              changed = true;
            }
          });
          return changed ? next : hours;
        };

        setEmployees((prev) =>
          prev.map((emp) => {
            const hours = dropRemovedHours(emp.hours);
            return hours === emp.hours ? emp : { ...emp, hours };
          })
        );
        setSavedHoursByEmployee((prev) => {
          const next = { ...(prev || {}) };
          let changed = false;
          Object.keys(next).forEach((empId) => {
            const hours = dropRemovedHours(
              next[empId],
              savedHourLabelsByEmployee[empId]
            );
            if (hours !== next[empId]) {
              next[empId] = hours;
              changed = true;
            }
          });
          return changed ? next : prev;
        });
        setSavedHourLabelsByEmployee((prev) => {
          const next = { ...(prev || {}) };
          let changed = false;
          Object.keys(next).forEach((empId) => {
            const labels = { ...(next[empId] || {}) };
            let rowChanged = false;
            Object.keys(labels).forEach((key) => {
              const label = String(labels[key] || '')
                .trim()
                .toLowerCase();
              const keyLabel = String(key || '').trim().toLowerCase();
              if (
                removedIds.has(String(key)) ||
                (keyLabel && removedLabels.has(keyLabel)) ||
                (label && removedLabels.has(label))
              ) {
                delete labels[key];
                rowChanged = true;
              }
            });
            if (rowChanged) {
              next[empId] = labels;
              changed = true;
            }
          });
          return changed ? next : prev;
        });
      }

      if (typesToAdd.length > 0) {
        const res = await axios.post(
          `${base}/api/miter/production-performance/${encodeURIComponent(
            projectCode
          )}/crew-timesheet-labor-types`,
          {
            workDate,
            scope: scopeName,
            scopeId: scopeId ?? null,
            laborTypes: typesToAdd,
          },
          auth
        );

        if (res.data?.status === 'success' && Array.isArray(res.data?.data?.laborTypes)) {
          crewLaborTypesRequestSeqRef.current += 1;
          setSavedLaborTypes(res.data.data.laborTypes);
        } else {
          await fetchCrewLaborTypes();
        }
      }

      setSelectedLaborTypes([]);
      setSelectedLaborTypesToRemove([]);
      setIsAddLaborOpen(false);

      let text = 'Labor types updated.';
      if (typesToAdd.length > 0 && typesToRemove.length === 0) {
        text = 'Labor type(s) added for selected work date.';
      } else if (typesToRemove.length > 0 && typesToAdd.length === 0) {
        text = hadHours
          ? 'Labor type(s) removed. Save and Log Equipment to update logged hours.'
          : 'Labor type(s) removed.';
      } else if (hadHours) {
        text =
          'Labor types updated. Save and Log Equipment to update logged hours.';
      }
      setMessage({ type: 'success', text });
    } catch (err) {
      setMessage({
        type: 'error',
        text:
          err.response?.data?.message || err.message || 'Failed to update labor types.',
      });
    } finally {
      setIsUpdatingLaborTypes(false);
    }
  };

  const persistRosterEmployees = async (membersToAdd, { successSingular, successPlural }) => {
    if (!projectCode || !workDate || !scopeName) {
      setMessage({
        type: 'error',
        text: 'Project, work date, and activity are required to save employees.',
      });
      return false;
    }

    const payloadEmployees = (membersToAdd || []).map((m) => ({
      id: m.id,
      name: m.name || 'Unknown',
      friendlyId: m.friendlyId || null,
      title: m.title || null,
      employmentStatus: m.employmentStatus || null,
      employmentType: m.employmentType || null,
    }));

    const base = process.env.REACT_APP_BACKEND_BASEURL;
    const res = await axios.post(
      `${base}/api/miter/production-performance/${encodeURIComponent(
        projectCode
      )}/crew-timesheet-employees`,
      {
        workDate,
        scope: scopeName,
        scopeId: scopeId ?? null,
        employees: payloadEmployees,
      },
      await getAuthConfig()
    );

    if (res.data?.status === 'success' && Array.isArray(res.data?.data?.employees)) {
      setSavedCrewEmployees(res.data.data.employees);
    } else {
      await fetchCrewEmployees();
    }

    setMessage({
      type: 'success',
      text:
        payloadEmployees.length === 1
          ? successSingular
          : successPlural.replace('{n}', String(payloadEmployees.length)),
    });
    return true;
  };

  const handleAddEmployeesUpdate = async () => {
    if (selectedEmployeesToAdd.length === 0) {
      setIsAddEmployeeOpen(false);
      return;
    }

    setIsAddingEmployees(true);
    setMessage(null);
    try {
      const ok = await persistRosterEmployees(selectedEmployeesToAdd, {
        successSingular: 'Employee saved for this work date.',
        successPlural: '{n} employees saved for this work date.',
      });
      if (ok) {
        setSelectedEmployeeIds([]);
        setIsAddEmployeeOpen(false);
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.message || err.message || 'Failed to save employees.',
      });
    } finally {
      setIsAddingEmployees(false);
    }
  };

  const buildEmployeeIdsForCrewLeads = (crewsToRemove) => {
    const ids = new Set();
    (crewsToRemove || []).forEach((lead) => {
      const leadId = lead?.id ? String(lead.id) : '';
      if (!leadId) return;
      if (existingEmployeeIds.has(leadId)) ids.add(leadId);
      (reportIdsByLeadId.get(leadId) || []).forEach((reportId) => {
        if (existingEmployeeIds.has(String(reportId))) {
          ids.add(String(reportId));
        }
      });
    });
    return Array.from(ids);
  };

  /** Update button: add-only runs immediately; remove opens Delete-style confirm first. */
  const requestCrewLeadsUpdate = () => {
    const crewsToAdd = selectedCrewLeadsToAdd;
    const crewsToRemove = selectedCrewLeadsToRemove;
    if (crewsToAdd.length === 0 && crewsToRemove.length === 0) {
      setIsAddCrewOpen(false);
      return;
    }
    if (crewsToRemove.length > 0) {
      const employeeIdsToRemove = buildEmployeeIdsForCrewLeads(crewsToRemove);
      setIsAddCrewOpen(false);
      setCrewRemoveConfirm({
        isOpen: true,
        crewsToAdd,
        crewsToRemove,
        employeeCount: employeeIdsToRemove.length || crewsToRemove.length,
      });
      return;
    }
    handleCrewLeadsUpdate(crewsToAdd, crewsToRemove);
  };

  const handleCloseCrewRemoveConfirm = () => {
    if (isUpdatingCrewLeads) return;
    setCrewRemoveConfirm({
      isOpen: false,
      crewsToAdd: [],
      crewsToRemove: [],
      employeeCount: 0,
    });
  };

  const handleConfirmCrewRemove = async () => {
    if (isUpdatingCrewLeads) return;
    const crewsToAdd = crewRemoveConfirm.crewsToAdd || [];
    const crewsToRemove = crewRemoveConfirm.crewsToRemove || [];
    await handleCrewLeadsUpdate(crewsToAdd, crewsToRemove);
    setCrewRemoveConfirm({
      isOpen: false,
      crewsToAdd: [],
      crewsToRemove: [],
      employeeCount: 0,
    });
  };

  const handleCrewLeadsUpdate = async (
    crewsToAddArg = selectedCrewLeadsToAdd,
    crewsToRemoveArg = selectedCrewLeadsToRemove
  ) => {
    const crewsToAdd = crewsToAddArg || [];
    const crewsToRemove = crewsToRemoveArg || [];

    if (crewsToAdd.length === 0 && crewsToRemove.length === 0) {
      setIsAddCrewOpen(false);
      return;
    }
    if (!projectCode || !workDate || !scopeName) {
      setMessage({
        type: 'error',
        text: 'Project, work date, and activity are required to update crews.',
      });
      return;
    }

    // Lead + every direct report currently on the sheet (same membership as Add Crew).
    const employeeIdsToRemove = buildEmployeeIdsForCrewLeads(crewsToRemove);

    setIsUpdatingCrewLeads(true);
    setMessage(null);
    // Ignore a roster GET that started before this update, or it can paint the
    // empty sheet back over the people we just changed.
    crewRosterRequestSeqRef.current += 1;
    const previousRoster = savedCrewEmployees;
    const leadIdsSnapshot = crewsToAdd.map((m) => String(m.id));
    const removeIdsSnapshot = [...employeeIdsToRemove];
    const removeLeadIdsSnapshot = crewsToRemove.map((m) => String(m.id));
    let paintedOptimistically = false;
    let removedCount = 0;
    let archivedCount = 0;
    let archiveErrorCount = 0;
    let travelMiterDeleted = 0;
    let travelMiterErrorCount = 0;
    let addedCount = 0;
    let addedLeadCount = 0;
    let requestedCount = 0;
    let lastEmployees = null;
    let trackingUpdate = null;

    try {
      if (employeeIdsToRemove.length > 0) {
        const res = await removeEmployeesBulkFromApi(employeeIdsToRemove);
        const data = res.data?.data || {};
        const removedIds = (
          Array.isArray(data.employeeIds) && data.employeeIds.length
            ? data.employeeIds
            : employeeIdsToRemove
        ).map(String);
        removedCount = removedIds.length;
        archivedCount += data.miterArchive?.archivedCount || 0;
        archiveErrorCount += (data.miterArchive?.errors || []).length;
        travelMiterDeleted += (data.miterTravel?.deleted || []).length;
        travelMiterErrorCount += (data.miterTravel?.errors || []).length;
        if (Array.isArray(data.employees)) {
          lastEmployees = data.employees;
          setSavedCrewEmployees(data.employees);
        }
        if (data.laborTracking || data.daysUsed) trackingUpdate = data;
        clearLocalEmployeeData(removedIds);
      }

      if (crewsToAdd.length > 0) {
        const rosterBeforeAdd = lastEmployees || savedCrewEmployees;
        const directoryReady = (teamMembersFromApi || []).some(
          (member) => member?.id && member?.reportsToId
        );
        if (directoryReady) {
          const existingIds = new Set(
            (rosterBeforeAdd || []).map((row) => String(row.id))
          );
          const startSort =
            (rosterBeforeAdd || []).reduce(
              (max, row) => Math.max(max, Number(row.sortOrder) || 0),
              0
            ) + 1;
          const additions = peopleForSelectedCrews(crewsToAdd, teamMembersFromApi)
            .filter((person) => person?.id && !existingIds.has(String(person.id)))
            .map((person, index) =>
              toSavedCrewEmployee(person, startSort + index)
            );
          if (additions.length > 0) {
            setSavedCrewEmployees([...(rosterBeforeAdd || []), ...additions]);
            paintedOptimistically = true;
          }
        }

        const base = process.env.REACT_APP_BACKEND_BASEURL;
        const res = await axios.post(
          `${base}/api/miter/production-performance/${encodeURIComponent(
            projectCode
          )}/crew-timesheet-employees/from-leads`,
          {
            workDate,
            scope: scopeName,
            scopeId: scopeId ?? null,
            leadIds: leadIdsSnapshot,
          },
          await getAuthConfig()
        );

        if (
          res.data?.status === 'success' &&
          Array.isArray(res.data?.data?.employees)
        ) {
          lastEmployees = res.data.data.employees;
          setSavedCrewEmployees(res.data.data.employees);
        } else if (!lastEmployees) {
          await fetchCrewEmployees();
        }

        const addedCountRaw = Number(res.data?.data?.addedCount);
        addedCount = Number.isFinite(addedCountRaw) ? addedCountRaw : 0;
        addedLeadCount = Number(res.data?.data?.addedLeadCount) || 0;
        const requestedCountRaw = Number(res.data?.data?.requestedCount);
        requestedCount = Number.isFinite(requestedCountRaw)
          ? requestedCountRaw
          : addedCount;
      } else if (lastEmployees == null && employeeIdsToRemove.length > 0) {
        await fetchCrewEmployees();
      }

      if (trackingUpdate) {
        const laborUpdate = {
          projectCode,
          scopeId: trackingUpdate.scopeId || scopeId,
          scopeName: trackingUpdate.scopeName || scopeName,
          laborTracking: trackingUpdate.laborTracking,
          daysUsed: trackingUpdate.daysUsed,
        };
        onLaborSaved(laborUpdate);
        window.dispatchEvent(
          new CustomEvent('nettles-labor-saved', { detail: laborUpdate })
        );
      }

      setSelectedCrewLeadIds([]);
      setSelectedCrewLeadIdsToRemove([]);
      setIsAddCrewOpen(false);

      const addLeadNames = crewsToAdd
        .map((m) => m.name)
        .filter(Boolean)
        .join(', ');
      const crewLabel = addLeadNames || 'selected crew';
      const leadNote =
        addedLeadCount > 0
          ? addedLeadCount === 1
            ? ', including the crew lead'
            : ', including the crew leads'
          : '';

      let text = 'Crews updated.';
      if (crewsToAdd.length > 0 && crewsToRemove.length === 0) {
        text =
          requestedCount === 0
            ? `No team members found for ${crewLabel}.`
            : addedCount === 0
              ? `Everyone on ${crewLabel} is already on this timesheet.`
              : addedCount === 1
                ? `Added 1 team member for ${crewLabel}${leadNote}.`
                : `Added ${addedCount} team members for ${crewLabel}${leadNote}.`;
      } else if (crewsToRemove.length > 0 && crewsToAdd.length === 0) {
        // Same wording as employee delete / Delete Multiple.
        const travelNote =
          travelMiterDeleted > 0
            ? ` and deleted ${travelMiterDeleted} Miter travel reimbursement(s)`
            : '';
        if (archiveErrorCount > 0 || travelMiterErrorCount > 0) {
          text =
            archivedCount === 0 && travelMiterDeleted === 0
              ? `Removed ${removedCount} employee${
                  removedCount === 1 ? '' : 's'
                } from this work date, but Miter did not fully sync the delete.`
              : `Removed ${removedCount}; Miter sync finished with ${
                  archiveErrorCount + travelMiterErrorCount
                } error(s).`;
        } else {
          text =
            archivedCount > 0
              ? `Removed ${removedCount} employee${
                  removedCount === 1 ? '' : 's'
                }, archived ${archivedCount} Miter timesheet(s)${travelNote}.`
              : travelMiterDeleted > 0
                ? `Removed ${removedCount} employee${
                    removedCount === 1 ? '' : 's'
                  }${travelNote}.`
                : `${removedCount} employee${
                    removedCount === 1 ? '' : 's'
                  } removed from this work date.`;
        }
      } else {
        const parts = [];
        if (addedCount > 0) {
          parts.push(
            addedCount === 1
              ? `added 1 team member`
              : `added ${addedCount} team members`
          );
        } else if (crewsToAdd.length > 0) {
          parts.push('no new team members to add');
        }
        if (removedCount > 0) {
          parts.push(
            removedCount === 1
              ? `removed 1 employee`
              : `removed ${removedCount} employees`
          );
        }
        if (archivedCount > 0) {
          parts.push(`archived ${archivedCount} Miter timesheet(s)`);
        }
        if (travelMiterDeleted > 0) {
          parts.push(
            `deleted ${travelMiterDeleted} Miter travel reimbursement(s)`
          );
        }
        text =
          parts.length > 0
            ? `Crews updated: ${parts.join('; ')}.`
            : 'Crews updated.';
      }

      setMessage({
        type:
          archiveErrorCount > 0 || travelMiterErrorCount > 0
            ? 'error'
            : 'success',
        text,
      });
    } catch (err) {
      // Remove (if any) already committed on the server — do not resurrect it.
      // Only roll back an optimistic add paint, keeping the post-remove roster.
      const hadRemove = removeIdsSnapshot.length > 0;
      if (paintedOptimistically || leadIdsSnapshot.length > 0 || hadRemove) {
        crewRosterRequestSeqRef.current += 1;
        if (paintedOptimistically) {
          setSavedCrewEmployees(
            removedCount > 0 && Array.isArray(lastEmployees)
              ? lastEmployees
              : previousRoster
          );
        }
        setSelectedCrewLeadIds(leadIdsSnapshot);
        setSelectedCrewLeadIdsToRemove(
          removedCount > 0 ? [] : removeLeadIdsSnapshot
        );
        // Remove uses the blocking confirm dialog — don't reopen the dropdown.
        if (!hadRemove) {
          setIsAddCrewOpen(true);
        }
      }
      setMessage({
        type: 'error',
        text:
          err.response?.data?.message ||
          err.message ||
          'Failed to update crews.',
      });
    } finally {
      setIsUpdatingCrewLeads(false);
    }
  };

  const handleCopyEmployeesFromDate = async (fromDateOverride) => {
    const fromDate = String(fromDateOverride || selectedCopyFromDate || '').trim();
    const dateInfo =
      (savedEmployeeDates || []).find((d) => String(d.workDate) === fromDate) || null;

    if (!fromDate || !dateInfo || fromDate === workDate) {
      return;
    }
    if (!projectCode || !workDate || !scopeName) {
      setMessage({
        type: 'error',
        text: 'Project, work date, and activity are required to copy employees.',
      });
      return;
    }

    setSelectedCopyFromDate(fromDate);
    setIsCopyingEmployees(true);
    setMessage(null);
    try {
      const base = process.env.REACT_APP_BACKEND_BASEURL;
      const res = await axios.post(
        `${base}/api/miter/production-performance/${encodeURIComponent(
          projectCode
        )}/crew-timesheet-employees/copy-from-date`,
        {
          fromWorkDate: fromDate,
          workDate,
          scope: scopeName,
          scopeId: scopeId ?? null,
        },
        await getAuthConfig()
      );

      if (res.data?.status === 'success' && Array.isArray(res.data?.data?.employees)) {
        setSavedCrewEmployees(res.data.data.employees);
      } else {
        await fetchCrewEmployees();
      }

      const copiedCount = Number(res.data?.data?.copiedCount) || 0;
      const fromLabel = formatWorkDateDisplay(
        res.data?.data?.fromWorkDate || fromDate
      );
      setSelectedCopyFromDate('');
      setIsCopyEmployeesOpen(false);
      setMessage({
        type: 'success',
        text:
          res.data?.message ||
          (copiedCount === 1
            ? `Copied 1 employee from ${fromLabel}.`
            : `Copied ${copiedCount} employees from ${fromLabel}.`),
      });
    } catch (err) {
      setMessage({
        type: 'error',
        text:
          err.response?.data?.message ||
          err.message ||
          'Failed to copy employees from the selected date.',
      });
    } finally {
      setIsCopyingEmployees(false);
    }
  };

  const handleOpenDeleteConfirm = (employee) => {
    if (!employee?.id || deletingEmployeeId) return;
    setDeleteConfirm({ isOpen: true, employees: [employee] });
  };

  const handleEnterSelectDeleteMode = () => {
    if (employees.length === 0 || deletingEmployeeId) return;
    setIsSelectDeleteMode(true);
  };

  const handleExitSelectDeleteMode = () => {
    if (deletingEmployeeId) return;
    setIsSelectDeleteMode(false);
    setSelectedTimesheetEmployeeIds([]);
  };

  const handleOpenBulkDeleteConfirm = () => {
    if (!isSelectDeleteMode) return;
    if (deletingEmployeeId || selectedTimesheetEmployeeIds.length === 0) return;
    const selectedSet = new Set(selectedTimesheetEmployeeIds.map(String));
    const toDelete = employees.filter((emp) => selectedSet.has(String(emp.id)));
    if (toDelete.length === 0) return;
    setDeleteConfirm({ isOpen: true, employees: toDelete });
  };

  const handleCloseDeleteConfirm = () => {
    if (deletingEmployeeId) return;
    setDeleteConfirm({ isOpen: false, employees: [] });
  };

  const clearLocalEmployeeData = (empIds) => {
    const ids = (empIds || []).map(String);
    if (ids.length === 0) return;
    const idSet = new Set(ids);
    setSavedHoursByEmployee((prev) => {
      if (!prev) return prev;
      let changed = false;
      const next = { ...prev };
      ids.forEach((id) => {
        if (next[id] != null) {
          delete next[id];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    setSavedHourLabelsByEmployee((prev) => {
      if (!prev) return prev;
      let changed = false;
      const next = { ...prev };
      ids.forEach((id) => {
        if (next[id] != null) {
          delete next[id];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    setSelectedTimesheetEmployeeIds((prev) =>
      prev.filter((id) => !idSet.has(String(id)))
    );
    // Drop saved travel too. Re-adding the person would otherwise restore
    // the checkboxes from this session even after Miter reimbursements are gone.
    const dropTravel = (prev) => {
      if (!prev) return prev;
      let changed = false;
      const next = { ...prev };
      ids.forEach((id) => {
        if (next[id] != null) {
          delete next[id];
          changed = true;
        }
      });
      return changed ? next : prev;
    };
    setSavedTravelByEmployee(dropTravel);
    setTravelByEmployeeId(dropTravel);
    // A travel GET that started before this delete must not write the old
    // checkboxes back over the cleared row.
    crewTravelRequestSeqRef.current += 1;
  };

  const removeOneEmployeeFromApi = async (empId) => {
    const base = process.env.REACT_APP_BACKEND_BASEURL;
    const params = new URLSearchParams({ scope: scopeName });
    if (scopeId) params.set('scopeId', String(scopeId));
    const res = await axios.delete(
      `${base}/api/miter/production-performance/${encodeURIComponent(
        projectCode
      )}/crew-timesheet-employees/${workDate}/${encodeURIComponent(empId)}?${params.toString()}`,
      await getAuthConfig()
    );
    return res;
  };

  const removeEmployeesBulkFromApi = async (employeeIds) => {
    const base = process.env.REACT_APP_BACKEND_BASEURL;
    const res = await axios.post(
      `${base}/api/miter/production-performance/${encodeURIComponent(
        projectCode
      )}/crew-timesheet-employees/${workDate}/bulk-delete`,
      {
        scope: scopeName,
        scopeId: scopeId ?? null,
        employeeIds: (employeeIds || []).map(String).filter(Boolean),
      },
      await getAuthConfig()
    );
    return res;
  };

  const handleConfirmRemoveEmployee = async () => {
    const toRemove = deleteConfirm.employees || [];
    if (toRemove.length === 0 || deletingEmployeeId) return;
    if (!projectCode || !workDate || !scopeName) {
      setMessage({
        type: 'error',
        text: 'Project, work date, and activity are required to remove an employee.',
      });
      setDeleteConfirm({ isOpen: false, employees: [] });
      return;
    }

    const isBulk = toRemove.length > 1;
    setDeletingEmployeeId(isBulk ? '__bulk__' : String(toRemove[0].id));
    setMessage(null);

    let lastEmployees = null;
    let trackingUpdate = null;
    let archivedCount = 0;
    let archiveErrorCount = 0;
    let travelMiterDeleted = 0;
    let travelMiterErrorCount = 0;
    let miterQueued = false;
    const removedIds = [];
    const failures = [];

    try {
      if (isBulk) {
        try {
          const res = await removeEmployeesBulkFromApi(
            toRemove.map((e) => e.id)
          );
          const data = res.data?.data || {};
          removedIds.push(
            ...((Array.isArray(data.employeeIds) && data.employeeIds.length
              ? data.employeeIds
              : toRemove.map((e) => String(e.id)))
            ).map(String)
          );
          archivedCount += data.miterArchive?.archivedCount || 0;
          archiveErrorCount += (data.miterArchive?.errors || []).length;
          travelMiterDeleted += (data.miterTravel?.deleted || []).length;
          travelMiterErrorCount += (data.miterTravel?.errors || []).length;
          if (data.miterSync === 'queued') miterQueued = true;
          if (Array.isArray(data.employees)) {
            lastEmployees = data.employees;
          }
          if (data.laborTracking || data.daysUsed) trackingUpdate = data;
        } catch (err) {
          failures.push({
            id: '__bulk__',
            name: `${toRemove.length} employees`,
            error:
              err.response?.data?.message ||
              err.message ||
              'Failed to remove employees',
          });
        }
      } else {
        for (const employee of toRemove) {
          const empId = String(employee.id);
          const empName = employee.name || 'Employee';
          try {
            const res = await removeOneEmployeeFromApi(empId);
            removedIds.push(empId);
            archivedCount += res.data?.data?.miterArchive?.archivedCount || 0;
            archiveErrorCount += (res.data?.data?.miterArchive?.errors || [])
              .length;
            travelMiterDeleted += (res.data?.data?.miterTravel?.deleted || []).length;
            travelMiterErrorCount += (res.data?.data?.miterTravel?.errors || []).length;
            if (Array.isArray(res.data?.data?.employees)) {
              lastEmployees = res.data.data.employees;
            }
            if (res.data?.data?.laborTracking || res.data?.data?.daysUsed) {
              trackingUpdate = res.data.data;
            }
          } catch (err) {
            failures.push({
              id: empId,
              name: empName,
              error:
                err.response?.data?.message ||
                err.message ||
                'Failed to remove',
            });
          }
        }
      }

      if (lastEmployees) {
        setSavedCrewEmployees(lastEmployees);
      } else if (removedIds.length > 0) {
        await fetchCrewEmployees();
      }

      clearLocalEmployeeData(removedIds);
      if (trackingUpdate) {
        const laborUpdate = {
          projectCode,
          scopeId: trackingUpdate.scopeId || scopeId,
          scopeName: trackingUpdate.scopeName || scopeName,
          laborTracking: trackingUpdate.laborTracking,
          daysUsed: trackingUpdate.daysUsed,
        };
        onLaborSaved(laborUpdate);
        window.dispatchEvent(new CustomEvent("nettles-labor-saved", { detail: laborUpdate }));
      }
      setDeleteConfirm({ isOpen: false, employees: [] });
      if (isBulk && removedIds.length > 0) {
        setIsSelectDeleteMode(false);
        setSelectedTimesheetEmployeeIds([]);
      }

      const removedCount = removedIds.length;
      if (removedCount === 0) {
        setMessage({
          type: 'error',
          text: failures[0]?.error || 'Failed to remove employee(s).',
        });
      } else if (miterQueued) {
        setMessage({
          type: 'success',
          text: `Removed ${
            isBulk ? `${removedCount} employees` : toRemove[0]?.name || 'employee'
          } from this work date. Miter timesheets and travel reimbursements are being removed.`,
        });
      } else if (failures.length > 0) {
        setMessage({
          type: 'error',
          text: `Removed ${removedCount}; ${failures.length} failed${
            archiveErrorCount
              ? ` (${archiveErrorCount} Miter archive error(s))`
              : ''
          }${
            travelMiterErrorCount
              ? ` (${travelMiterErrorCount} Miter travel delete error(s))`
              : ''
          }.`,
        });
      } else if (
        (archiveErrorCount > 0 && archivedCount === 0) ||
        (travelMiterErrorCount > 0 && travelMiterDeleted === 0)
      ) {
        setMessage({
          type: 'error',
          text: `Removed ${
            isBulk ? `${removedCount} employees` : toRemove[0]?.name || 'employee'
          } from this work date, but Miter did not fully sync the delete.`,
        });
      } else if (archiveErrorCount > 0 || travelMiterErrorCount > 0) {
        setMessage({
          type: 'error',
          text: `Removed ${removedCount}; Miter sync finished with ${
            archiveErrorCount + travelMiterErrorCount
          } error(s).`,
        });
      } else {
        const travelNote =
          travelMiterDeleted > 0
            ? ` and deleted ${travelMiterDeleted} Miter travel reimbursement(s)`
            : '';
        setMessage({
          type: 'success',
          text:
            archivedCount > 0
              ? `Removed ${
                  isBulk
                    ? `${removedCount} employees`
                    : toRemove[0]?.name || 'employee'
                }, archived ${archivedCount} Miter timesheet(s)${travelNote}.`
              : travelMiterDeleted > 0
                ? `Removed ${
                    isBulk
                      ? `${removedCount} employees`
                      : toRemove[0]?.name || 'employee'
                  }${travelNote}.`
                : isBulk
                  ? `${removedCount} employees removed from this work date.`
                  : `${toRemove[0]?.name || 'Employee'} removed from this work date.`,
        });
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text:
          err.response?.data?.message ||
          err.message ||
          'Failed to remove employee(s).',
      });
    } finally {
      setDeletingEmployeeId(null);
    }
  };

  const selectedTimesheetEmployeeIdSet = useMemo(
    () => new Set(selectedTimesheetEmployeeIds.map(String)),
    [selectedTimesheetEmployeeIds]
  );

  const allEmployeeIds = useMemo(
    () => employees.map((emp) => String(emp.id)).filter(Boolean),
    [employees]
  );

  const selectedCount = useMemo(
    () =>
      allEmployeeIds.filter((id) => selectedTimesheetEmployeeIdSet.has(id))
        .length,
    [allEmployeeIds, selectedTimesheetEmployeeIdSet]
  );

  const allSelected =
    allEmployeeIds.length > 0 && selectedCount === allEmployeeIds.length;
  const someSelected = selectedCount > 0 && !allSelected;

  useEffect(() => {
    const el = selectAllCheckboxRef.current;
    if (el) el.indeterminate = someSelected;
  }, [someSelected]);

  // Drop selections for employees no longer on the roster.
  useEffect(() => {
    const valid = new Set(allEmployeeIds);
    setSelectedTimesheetEmployeeIds((prev) => {
      const next = prev.filter((id) => valid.has(String(id)));
      return next.length === prev.length ? prev : next;
    });
  }, [allEmployeeIds]);

  const handleToggleEmployeeSelected = (employeeId, checked) => {
    const id = String(employeeId);
    setSelectedTimesheetEmployeeIds((prev) => {
      const has = prev.some((x) => String(x) === id);
      if (checked && !has) return [...prev, id];
      if (!checked && has) return prev.filter((x) => String(x) !== id);
      return prev;
    });
  };

  const handleToggleSelectAll = (checked) => {
    setSelectedTimesheetEmployeeIds(checked ? [...allEmployeeIds] : []);
  };

  // Only hours already stored for this date. Typed-but-not-saved cells must not
  // open a Production Tracking row.
  const hasSavedCrewProjectLabor = Object.values(savedHoursByEmployee || {}).some((hours) => {
    if (!hours || typeof hours !== 'object') return false;
    const projectHours = Object.entries(hours).reduce((sum, [columnId, value]) => {
      if (NON_PROJECT_COLUMN_IDS.has(columnId)) return sum;
      return sum + toNumber(value);
    }, 0);
    return projectHours > 0;
  });

  const buildFlowContext = () => ({
    projectNo: projectCode,
    projectName: projectName ?? '',
    workDate,
    division: scopeName ?? '',
    scopeId:
      activeRowData?.scopeId ??
      activeRowData?.originalData?.scopeId ??
      '',
    projectActivities,
    hasCrewProjectLabor: hasSavedCrewProjectLabor,
  });

  const goToFlowStep = (step) => {
    if (usingFlowNav) {
      onFlowNavigate(step, buildFlowContext());
      return;
    }
    setIsEquipmentTimeCardOpen(step === 'equipment');
  };

  const handleSaveToMiter = async () => {
    if (!projectCode || !workDate || !scopeName) {
      setMessage({
        type: 'error',
        text: 'Project and work date are required.',
      });
      return;
    }

    // Empty crew and travel tables have nothing to persist. Open Equipment
    // Time Card without calling Miter.
    if (employees.length === 0) {
      setMessage(null);
      goToFlowStep('equipment');
      return;
    }

    if (!scopeId || !isMiterActivity) {
      setMessage({
        type: 'error',
        text: 'Project, work date, and a Miter activity are required to save.',
      });
      return;
    }

    // Miter entries: Project Labor total only (never Non-Project hours).
    const entries = employees
      .map((emp) => ({
        teamMemberId: emp.id,
        teamMemberName: emp.name,
        hours: getProjectLaborHours(emp),
      }))
      .filter((e) => e.teamMemberId && e.hours > 0);

    const laborLabels = {};
    allHourColumns.forEach((col) => {
      laborLabels[col.id] = col.label;
    });

    // Local grid still stores all columns (Project Labor + Non-Project) for the table UI.
    const grid = employees.map((emp) => ({
      employeeId: emp.id,
      employeeName: emp.name,
      hours: { ...(emp.hours || {}) },
      laborLabels,
    }));

    const travel = employees.map((emp) => {
      const id = String(emp.id);
      const row =
        travelByEmployeeId[id] ||
        buildEmptyTravelEntry(getEmployeeDefaultMiles(id));
      return {
        employeeId: emp.id,
        employeeName: emp.name,
        overrideDefaultMiles: Boolean(row.overrideDefaultMiles),
        miles: toTravelMilesNumber(row.miles),
        flags: { ...(row.flags || {}) },
        amount: recomputeTravelAmount(row),
        miterIds:
          row.miterIds && typeof row.miterIds === 'object'
            ? { ...row.miterIds }
            : {},
      };
    });

    if (entries.length === 0 && grid.length === 0) {
      setMessage({
        type: 'error',
        text: 'Enter Project Labor hours for at least one employee before saving.',
      });
      return;
    }

    setSaving(true);
    setMessage(null);
    try {
      const base = process.env.REACT_APP_BACKEND_BASEURL;
      const res = await axios.post(
        `${base}/api/miter/production-performance/${encodeURIComponent(
          projectCode
        )}/crew-timesheet/save`,
        {
          workDate,
          scope: scopeName,
          scopeId,
          entries,
          grid,
          travel,
        },
        await getAuthConfig()
      );

      // Keep local table in sync with what we just persisted.
      const hoursMap = {};
      const labelsMap = {};
      const hourRows = res.data?.data?.hoursGrid;
      if (Array.isArray(hourRows)) {
        hourRows.forEach((row) => {
          if (!row?.employeeId) return;
          const empId = String(row.employeeId);
          hoursMap[empId] = row.hours || {};
          labelsMap[empId] = row.laborLabels || {};
        });
        setSavedHoursByEmployee(hoursMap);
        setSavedHourLabelsByEmployee(labelsMap);
      }

      // Trust what the user just saved for checkboxes. Background Miter sync
      // may still be writing ids — a short refetch picks those up without
      // wiping Employee Paid / Room Shared selections.
      const localMap = {};
      travel.forEach((row) => {
        localMap[String(row.employeeId)] = row;
      });
      const travelRows = res.data?.data?.travelGrid;
      if (Array.isArray(travelRows)) {
        travelRows.forEach((row) => {
          if (!row?.employeeId) return;
          const id = String(row.employeeId);
          const sent = localMap[id] || {};
          localMap[id] = {
            ...sent,
            ...row,
            flags: {
              ...(sent.flags || {}),
              ...(row.flags || {}),
            },
            miterIds: {
              ...(sent.miterIds || {}),
              ...(row.miterIds || {}),
            },
          };
        });
      }
      travelEditedRef.current = false;
      setSavedTravelByEmployee(localMap);
      crewTravelRefreshCountRef.current = 0;
      window.setTimeout(() => {
        fetchCrewTravel();
      }, 8000);

      if (res.data?.status === 'success' && (res.data?.data?.laborTracking || res.data?.data?.daysUsed)) {
        const laborUpdate = {
          projectCode,
          scopeId,
          scopeName,
          laborTracking: res.data.data.laborTracking,
          daysUsed: res.data.data.daysUsed,
        };
        onLaborSaved(laborUpdate);
        window.dispatchEvent(new CustomEvent("nettles-labor-saved", { detail: laborUpdate }));
      }

      const data = res.data?.data;
      const np = data?.nonProject || {};
      const createErrors = [
        ...(data?.createErrors || []),
        ...(np.createErrors || []),
      ];
      const updateErrors = [
        ...(data?.updateErrors || []),
        ...(np.updateErrors || []),
      ];
      const travelErrors = data?.travelMiter?.errors || [];
      const failCount =
        createErrors.length +
        updateErrors.length +
        (np.resolveErrors?.length || 0) +
        travelErrors.length;
      const createdCount =
        (data?.created?.length || 0) + (np.created?.length || 0);
      const updatedCount =
        (data?.updated?.length || 0) + (np.updated?.length || 0);
      const archivedCount =
        (data?.archived?.length || 0) + (np.archived?.length || 0);
      const unchangedCount = [...(data?.skipped || []), ...(np.skipped || [])].filter(
        (s) => s.reason === 'hours unchanged'
      ).length;
      const travelOkCount =
        (data?.travelMiter?.created?.length || 0) +
        (data?.travelMiter?.updated?.length || 0) +
        (data?.travelMiter?.deleted?.length || 0);
      const successCount =
        createdCount + updatedCount + archivedCount + unchangedCount + travelOkCount;
      const firstError =
        createErrors[0]?.error ||
        updateErrors[0]?.error ||
        np.resolveErrors?.[0] ||
        travelErrors[0] ||
        null;

      if (res.data?.status === 'error' || (failCount > 0 && successCount === 0)) {
        setMessage({
          type: 'error',
          text: firstError || res.data?.message || 'Failed to save hours to Miter.',
        });
      } else if (failCount > 0) {
        setMessage({
          type: 'error',
          text: `${res.data?.message || `Saved with ${failCount} error(s).`} ${
            firstError || ''
          }`.trim(),
        });
        goToFlowStep('equipment');
      } else {
        goToFlowStep('equipment');
        setMessage({
          type: 'success',
          text:
            res.data?.message ||
            `Saved to Miter (created ${createdCount}, updated ${updatedCount}${
              archivedCount ? `, archived ${archivedCount}` : ''
            }).`,
        });
      }
    } catch (err) {
      const status = err.response?.status;
      const apiMessage = err.response?.data?.message || err.response?.data?.error;
      let text =
        apiMessage ||
        err.message ||
        'Failed to save crew timesheet to Miter.';
      if (status === 429 || /status code 429/i.test(String(text))) {
        text =
          'Miter is rate-limiting requests (too many at once). Please wait a few seconds and click Save again.';
      }
      setMessage({
        type: 'error',
        text,
      });
    } finally {
      setSaving(false);
    }
  };

  const equipmentOpen = usingFlowNav ? dailyLogStep === 'equipment' : isEquipmentTimeCardOpen;
  const crewSheetOpen = usingFlowNav
    ? dailyLogStep === 'crew' || dailyLogStep == null
    : !isEquipmentTimeCardOpen;

  if (!isOpen) return null;

  const projectLaborCount = Math.max(projectLaborColumns.length, 1);
  const hourColumnCount = projectLaborCount + NON_PROJECT_TYPES.length;
  const hoursTableMinRem =
    (isSelectDeleteMode ? 2.5 : 0) + 2.5 + 18 + hourColumnCount * 6.75 + 5.75;
  const deleteTargets = deleteConfirm.employees || [];
  const isBulkDelete = deleteTargets.length > 1;
  const deleteEmployeeName =
    deleteTargets.length === 0
      ? 'this employee'
      : isBulkDelete
        ? `${deleteTargets.length} employees`
        : deleteTargets[0]?.name || 'this employee';
  const deleteConfirmMessage = `Are you sure you want to delete ${deleteEmployeeName} from this crew timesheet? Matching Miter timesheets for this work date will be archived, and saved Travel Reimbursement entries for ${isBulkDelete ? 'those employees' : 'this employee'} will be deleted from Miter.`;

  // Freeze trash + Employee on all viewports (mobile browser + PWA) while labor cols scroll.
  const stickySelectCol = 'w-10 sticky left-0 z-20';
  const stickyTrashCol = isSelectDeleteMode
    ? 'w-10 sticky left-10 z-20'
    : 'w-10 sticky left-0 z-20';
  const stickyEmployeeShadow =
    'shadow-[4px_0_8px_-4px_rgba(15,23,42,0.2)]';
  const stickyEmployeeCol = isSelectDeleteMode
    ? `min-w-[14rem] md:min-w-[18rem] sticky left-20 z-20 ${stickyEmployeeShadow}`
    : `min-w-[14rem] md:min-w-[18rem] sticky left-10 z-20 ${stickyEmployeeShadow}`;
  const stickyTravelEmployeeCol = `min-w-[14rem] md:min-w-[18rem] sticky left-0 z-20 ${stickyEmployeeShadow}`;
  const hoursColgroup = (
    <colgroup>
      {isSelectDeleteMode ? <col className="nettles-crew-col-select" /> : null}
      <col className="nettles-crew-col-trash" />
      <col className="nettles-crew-col-employee" />
      {(projectLaborColumns.length === 0 ? [{ id: 'empty-labor' }] : projectLaborColumns).map(
        (col) => <col key={col.id} className="nettles-crew-col-hour" />
      )}
      {NON_PROJECT_TYPES.map((col) => (
        <col key={col.id} className="nettles-crew-col-hour" />
      ))}
      <col className="nettles-crew-col-total" />
    </colgroup>
  );

  return (
    <>
      {crewSheetOpen &&
        createPortal(
        <div className="fixed inset-0 z-[10160] flex items-stretch justify-center bg-black/50">
          {/* inset-2 phones/tablets; safe-area for notch/home; lg+ desktop inset-6 */}
          <div
            className="nettles-flow-sheet-panel absolute inset-2 lg:inset-6 bg-white rounded-xl shadow-2xl flex flex-col overflow-x-clip overflow-y-hidden max-lg:top-[max(0.5rem,env(safe-area-inset-top))] max-lg:bottom-[max(0.5rem,env(safe-area-inset-bottom))] max-lg:left-[max(0.5rem,env(safe-area-inset-left))] max-lg:right-[max(0.5rem,env(safe-area-inset-right))]"
          >
            <div className="relative z-10 flex shrink-0 items-center justify-between gap-3 sm:gap-4 bg-white py-3.5 sm:py-[18px] px-4 sm:px-6 rounded-t-xl border-b border-gray-200 shadow-[0_2px_5px_rgba(0,0,0,0.10)]">
              {usingFlowNav ? (
                <DailyLogBreadcrumbs
                  currentStep="crew"
                  onStepChange={goToFlowStep}
                  disabled={saving}
                />
              ) : (
                <h2 className="font-poppins text-[18px] sm:text-[20px] font-bold text-[#2C2C2C]">
                  Crew Timesheet
                </h2>
              )}
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="flex items-center justify-center w-10 h-10 sm:w-9 sm:h-9 rounded-md hover:bg-gray-100 touch-manipulation"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  className="shrink-0"
                >
                  <path
                    d="M17.7072 0.292849C17.3167 -0.0976164 16.6836 -0.0976164 16.2932 0.292849L8.99999 7.58604L1.70683 0.292849C1.31637 -0.0976164 0.683306 -0.0976164 0.29285 0.292849C-0.0976166 0.683305 -0.0976166 1.31637 0.29285 1.70683L7.58602 9.00001L0.29287 16.2931C-0.0975966 16.6836 -0.0975966 17.3166 0.29287 17.7072C0.683326 18.0976 1.31639 18.0976 1.70685 17.7072L8.99999 10.414L16.2932 17.7072C16.6836 18.0976 17.3167 18.0976 17.7072 17.7072C18.0976 17.3166 18.0976 16.6836 17.7072 16.2932L10.414 9.00001L17.7072 1.70683C18.0976 1.31637 18.0976 0.683305 17.7072 0.292849Z"
                    fill="#424143"
                  />
                </svg>
              </button>
            </div>

            <div className="nettles-flow-sheet-scroll flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden max-lg:landscape:overflow-y-auto max-lg:landscape:[scrollbar-gutter:stable] max-lg:landscape:[-webkit-overflow-scrolling:touch]">
            <div className="nettles-flow-sheet-body flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-clip max-lg:landscape:flex-none max-lg:landscape:overflow-y-visible">
              <DailyLogProjectFields
                projectNo={displayProjectCode}
                projectName={displayProjectName}
                workDate={workDate}
                onWorkDateChange={setWorkDate}
                activity={displayActivity}
              />
              {!isMiterActivity && !activityLoading && (
                <p className="px-4 md:px-6 pt-2 text-sm text-amber-700">
                  This row is not linked to a Miter activity — hours can be entered, but
                  Save to Miter requires a Miter activity row.
                </p>
              )}

              <div className="min-w-0 px-4 md:px-6 pt-3 pb-4">
          <div className="flex flex-col gap-3 mb-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 flex-1">
              <h3 className="text-base font-semibold text-gray-800">Crew Time Sheet</h3>
              <p className="text-xs text-gray-500 mt-0.5 leading-snug sm:leading-normal">
                <span className="font-medium text-[#1E5A8A]">
                  {employees.length}{' '}
                  {employees.length === 1 ? 'employee' : 'employees'}
                </span>
                <span className="mx-1.5 text-gray-300" aria-hidden>
                  ·
                </span>
                Enter hours by labor type. Save pushes Project Labor to this job,
                and Non-Project (Warehouse / Equip. Repair) to their separate X-code
                jobs in Miter.
              </p>
            </div>
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:items-end shrink-0">
              {jobLoading && (
                <span className="text-xs text-gray-500 self-start sm:self-end">
                  Loading…
                </span>
              )}
              {isSelectDeleteMode ? (
                <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-3">
                  <span className="text-sm text-gray-600 sm:whitespace-nowrap">
                    {selectedCount > 0
                      ? `${selectedCount} selected`
                      : 'Select employees to delete'}
                  </span>
                  <div className="grid grid-cols-2 gap-2 sm:flex sm:gap-3">
                    <button
                      type="button"
                      onClick={handleExitSelectDeleteMode}
                      disabled={Boolean(deletingEmployeeId)}
                      className="h-10 sm:h-9 px-4 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleOpenBulkDeleteConfirm}
                      disabled={
                        Boolean(deletingEmployeeId) || selectedCount === 0
                      }
                      className="h-10 sm:h-9 px-4 rounded-md border border-red-200 bg-white text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                    >
                      Delete selected
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex w-full flex-wrap items-center justify-between gap-2 sm:w-auto sm:justify-end sm:gap-4">
                  <button
                    type="button"
                    onClick={handleEnterSelectDeleteMode}
                    disabled={employees.length === 0 || Boolean(deletingEmployeeId)}
                    className="h-10 sm:h-9 px-4 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Delete Multiple
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsUpdateMultipleOpen(true)}
                    disabled={employees.length === 0 || allHourColumns.length === 0}
                    className="flex items-center gap-2 text-[15px] sm:text-[16px] font-normal text-[#AD2226] underline font-poppins leading-none focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      className="w-6 h-6"
                      fill="none"
                      aria-hidden
                    >
                      <path
                        d="M10 6C10 7.10457 10.8954 8 12 8C13.1046 8 14 7.10457 14 6C14 4.89543 13.1046 4 12 4C10.8954 4 10 4.89543 10 6Z"
                        fill="#AD2226"
                      />
                      <path
                        d="M10 12C10 13.1046 10.8954 14 12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12Z"
                        fill="#AD2226"
                      />
                      <path
                        d="M12 20C10.8954 20 10 19.1046 10 18C10 16.8954 10.8954 16 12 16C13.1046 16 14 16.8954 14 18C14 19.1046 13.1046 20 12 20Z"
                        fill="#AD2226"
                      />
                    </svg>
                    <span>Update Multiple</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          <PinnedColumnHScroll
            measureKey={`${isSelectDeleteMode ? 'select' : 'view'}-${employees.length}-${projectLaborColumns.length}`}
            footer={
              employees.length > 0 ? (
                <table
                  className="nettles-crew-hours-table border-separate border-spacing-0 text-sm"
                  style={{ minWidth: `${hoursTableMinRem}rem` }}
                >
                  {hoursColgroup}
                  <tbody>
                    <tr>
                      {isSelectDeleteMode && (
                        <td className={`${stickySelectCol} bg-[#F5F0E8] px-1 py-3`} />
                      )}
                      <td className={`${stickyTrashCol} bg-[#F5F0E8] px-1 py-3`} />
                      <td
                        className={`${stickyEmployeeCol} bg-[#F5F0E8] px-3 sm:px-4 py-3 text-sm font-bold text-[#2C2C2C] border-r border-gray-200`}
                      >
                        Crew Total
                      </td>
                      {projectLaborColumns.length === 0 ? (
                        <td className="nettles-crew-hour-cell bg-[#F5F0E8] border-r border-gray-200 px-2 py-3 text-center text-sm font-semibold text-gray-400">
                          —
                        </td>
                      ) : (
                        projectLaborColumns.map((col, idx) => (
                          <td
                            key={col.id}
                            className={`nettles-crew-hour-cell bg-[#F5F0E8] px-2 py-3 text-center text-sm font-semibold text-[#2C2C2C] ${
                              idx === projectLaborColumns.length - 1
                                ? 'border-r border-gray-200'
                                : ''
                            }`}
                          >
                            {columnTotals[col.id] || 0}
                          </td>
                        ))
                      )}
                      {NON_PROJECT_TYPES.map((col, idx) => (
                        <td
                          key={col.id}
                          className={`nettles-crew-hour-cell bg-[#F5F0E8] px-2 py-3 text-center text-sm font-semibold text-[#2C2C2C] ${
                            idx === NON_PROJECT_TYPES.length - 1
                              ? 'border-r border-gray-200'
                              : ''
                          }`}
                        >
                          {columnTotals[col.id] || 0}
                        </td>
                      ))}
                      <td className="bg-[#F5F0E8] px-3 py-3 text-center text-base font-bold text-[#2563EB]">
                        {laborGrandTotal}
                      </td>
                    </tr>
                  </tbody>
                </table>
              ) : null
            }
          >
            <table
              className="nettles-crew-hours-table border-separate border-spacing-0 text-sm"
              style={{ minWidth: `${hoursTableMinRem}rem` }}
            >
              {hoursColgroup}
              <thead>
                <tr className="bg-white">
                  {isSelectDeleteMode && (
                    <th
                      rowSpan={2}
                      className={`${stickySelectCol} bg-white px-2 py-3 text-center text-xs font-semibold text-gray-600 border-b border-gray-200`}
                    >
                      <input
                        ref={selectAllCheckboxRef}
                        type="checkbox"
                        checked={allSelected}
                        onChange={(e) => handleToggleSelectAll(e.target.checked)}
                        disabled={
                          employees.length === 0 || Boolean(deletingEmployeeId)
                        }
                        aria-label="Select all employees"
                        className="h-4 w-4 rounded border-gray-300 text-[#AD2226] focus:ring-[#AD2226] disabled:opacity-50"
                      />
                    </th>
                  )}
                  <th
                    rowSpan={2}
                    className={`${stickyTrashCol} bg-white px-2 py-3 text-center text-xs font-semibold text-gray-600 border-b border-gray-200`}
                    aria-label="Delete"
                  />
                  <th
                    rowSpan={2}
                    className={`${stickyEmployeeCol} bg-white px-3 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 border-b border-r border-gray-200`}
                  >
                    Employee
                  </th>
                  <th
                    colSpan={projectLaborCount}
                    className="bg-[#D6E8F5] px-2 py-2 text-center text-xs font-bold text-[#1E5A8A] border-b border-r border-gray-200"
                  >
                    Project Labor
                  </th>
                  <th
                    colSpan={NON_PROJECT_TYPES.length}
                    className="bg-[#F5E6D3] px-2 py-2 text-center text-xs font-bold text-[#8B5A2B] border-b border-r border-gray-200"
                  >
                    Non-Project
                  </th>
                  <th
                    rowSpan={2}
                    className="bg-white px-2 py-3 text-center text-xs font-semibold text-gray-600 border-b border-gray-200"
                  >
                    Total
                  </th>
                </tr>
                <tr className="bg-white">
                  {projectLaborColumns.length === 0 ? (
                    <th className="nettles-crew-hour-head bg-[#EAF4FB] px-1.5 py-2 text-center text-[11px] font-medium text-gray-500 border-b border-r border-gray-200">
                      —
                    </th>
                  ) : (
                    projectLaborColumns.map((col, idx) => (
                      <th
                        key={col.id}
                        className={`nettles-crew-hour-head bg-[#EAF4FB] px-1.5 py-2 text-center text-[11px] font-medium text-gray-700 border-b ${
                          idx === projectLaborColumns.length - 1
                            ? 'border-r border-gray-200'
                            : ''
                        }`}
                      >
                        {col.label}
                      </th>
                    ))
                  )}
                  {NON_PROJECT_TYPES.map((col, idx) => (
                    <th
                      key={col.id}
                      className={`nettles-crew-hour-head bg-[#FAF0E4] px-1.5 py-2 text-center text-[11px] font-medium text-gray-700 border-b ${
                        idx === NON_PROJECT_TYPES.length - 1
                          ? 'border-r border-gray-200'
                          : ''
                      }`}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {employees.length === 0 && (
                  <tr>
                    <td
                      colSpan={
                        projectLaborCount +
                        NON_PROJECT_TYPES.length +
                        (isSelectDeleteMode ? 4 : 3)
                      }
                      className="px-4 py-8 text-center text-gray-500"
                    >
                      No employees yet. Use Add/Remove Crew, Copy Employees from Another
                      Date, or Add Employee for this work date.
                    </td>
                  </tr>
                )}
                {employees.map((emp, rowIdx) => {
                  const rowTotal = getLaborRowTotal(emp);
                  const empId = String(emp.id);
                  const isDeleting =
                    deletingEmployeeId === empId ||
                    deletingEmployeeId === '__bulk__';
                  const isSelected =
                    isSelectDeleteMode &&
                    selectedTimesheetEmployeeIdSet.has(empId);
                  const bandBg = rowIdx % 2 === 0 ? 'bg-white' : 'bg-slate-100';
                  const stickyBg = isSelected ? 'bg-red-50' : bandBg;
                  return (
                    <tr
                      key={emp.id}
                      className={`border-t border-gray-100 ${
                        isSelected ? 'bg-red-50/40' : bandBg
                      }`}
                    >
                      {isSelectDeleteMode && (
                        <td
                          className={`${stickySelectCol} ${stickyBg} px-2 py-2.5 text-center`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) =>
                              handleToggleEmployeeSelected(
                                empId,
                                e.target.checked
                              )
                            }
                            disabled={Boolean(deletingEmployeeId)}
                            aria-label={`Select ${emp.name || 'employee'}`}
                            className="h-4 w-4 rounded border-gray-300 text-[#AD2226] focus:ring-[#AD2226] disabled:opacity-50"
                          />
                        </td>
                      )}
                      <td
                        className={`${stickyTrashCol} ${stickyBg} px-1 py-2.5 text-center`}
                      >
                        <button
                          type="button"
                          onClick={() => handleOpenDeleteConfirm(emp)}
                          disabled={Boolean(deletingEmployeeId)}
                          title={`Remove ${emp.name || 'employee'}`}
                          aria-label={`Remove ${emp.name || 'employee'}`}
                          className="inline-flex h-8 w-8 items-center justify-center rounded text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <FiTrash2
                            className={`h-4 w-4 ${isDeleting ? 'animate-pulse' : ''}`}
                          />
                        </button>
                      </td>
                      <td
                        className={`${stickyEmployeeCol} ${stickyBg} px-3 sm:px-4 py-2.5 border-r border-gray-200`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span
                            className="hidden md:inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#D6E8F5] text-xs font-semibold text-[#1E5A8A]"
                            aria-hidden
                          >
                            {getInitials(emp.name)}
                          </span>
                          <span className="whitespace-nowrap font-medium text-[#2C2C2C] text-sm sm:text-[15px]">
                            {emp.name}
                          </span>
                        </div>
                      </td>

                      {projectLaborColumns.length === 0 ? (
                        <td className="nettles-crew-hour-cell border-r border-gray-200 px-2 py-2.5 text-center text-gray-400">
                          —
                        </td>
                      ) : (
                        projectLaborColumns.map((col, idx) => (
                          <td
                            key={col.id}
                            className={`nettles-crew-hour-cell px-2 py-2.5 text-center ${
                              idx === projectLaborColumns.length - 1
                                ? 'border-r border-gray-200'
                                : ''
                            }`}
                          >
                            <input
                              type="text"
                              inputMode="decimal"
                              value={emp.hours?.[col.id] ?? 0}
                              onChange={(e) =>
                                handleHoursChange(emp.id, col.id, e.target.value)
                              }
                              onFocus={(e) => e.target.select()}
                              className="h-10 w-full rounded-md border border-gray-300 bg-white px-1.5 text-center text-sm text-[#2C2C2C] touch-manipulation focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </td>
                        ))
                      )}

                      {NON_PROJECT_TYPES.map((col, idx) => (
                        <td
                          key={col.id}
                          className={`nettles-crew-hour-cell px-2 py-2.5 text-center ${
                            idx === NON_PROJECT_TYPES.length - 1
                              ? 'border-r border-gray-200'
                              : ''
                          }`}
                        >
                          <input
                            type="text"
                            inputMode="decimal"
                            value={emp.hours?.[col.id] ?? 0}
                            onChange={(e) =>
                              handleHoursChange(emp.id, col.id, e.target.value)
                            }
                            onFocus={(e) => e.target.select()}
                            className="h-10 w-full rounded-md border border-gray-300 bg-white px-1.5 text-center text-sm text-[#2C2C2C] touch-manipulation focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </td>
                      ))}

                      <td className="px-3 py-3 text-center text-base font-bold text-[#2563EB]">
                        {rowTotal}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </PinnedColumnHScroll>

          <div className="mt-4 grid grid-cols-2 gap-2 md:flex md:flex-wrap md:items-center md:gap-3">
            <div className="relative col-span-2 min-w-0 md:col-auto">
              <button
                ref={addCrewBtnRef}
                type="button"
                onClick={() => {
                  setIsAddLaborOpen(false);
                  setIsAddEmployeeOpen(false);
                  setIsCopyEmployeesOpen(false);
                  setIsAddCrewOpen((v) => !v);
                }}
                className="h-11 md:h-10 w-full md:w-auto px-4 md:px-6 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 touch-manipulation"
              >
                Add/Remove Crew
              </button>

              {isAddCrewOpen &&
                addCrewPopupPos &&
                createPortal(
                  <>
                    <div
                      className="fixed inset-0 z-[10240]"
                      onClick={() => {
                        if (!isUpdatingCrewLeads) setIsAddCrewOpen(false);
                      }}
                    />
                    <div
                      className="fixed z-[10250] rounded-lg bg-white border border-gray-200 shadow-xl"
                      style={{
                        left: addCrewPopupPos.left,
                        bottom: addCrewPopupPos.bottom,
                        width: addCrewPopupPos.width,
                      }}
                    >
                      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                        <h3 className="text-sm font-semibold text-gray-900">
                          Add/Remove Crew
                        </h3>
                        <button
                          type="button"
                          onClick={() => {
                            if (!isUpdatingCrewLeads) setIsAddCrewOpen(false);
                          }}
                          disabled={isUpdatingCrewLeads}
                          className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                          aria-label="Close"
                        >
                          ✕
                        </button>
                      </div>
                      <div className="px-4 py-2 border-b border-gray-100">
                        <input
                          type="search"
                          value={crewLeadSearchQuery}
                          onChange={(e) => setCrewLeadSearchQuery(e.target.value)}
                          placeholder="Search by name or employee ID…"
                          className="w-full h-9 px-3 rounded-md border border-gray-300 text-sm"
                        />
                      </div>
                      <div className="px-4 py-3 max-h-[min(360px,50dvh)] overflow-y-auto overscroll-contain">
                        {crewLeadsLoading && (
                          <p className="text-sm text-gray-500">Loading crew leads…</p>
                        )}
                        {crewLeadsError && (
                          <p className="text-sm text-red-600">{crewLeadsError}</p>
                        )}
                        {!crewLeadsLoading &&
                          !crewLeadsError &&
                          crewLeadsFromApi.length === 0 && (
                            <p className="text-sm text-red-600">
                              Crew leads are temporarily unavailable. Please try again.
                            </p>
                          )}
                        {!crewLeadsLoading &&
                          !crewLeadsError &&
                          crewLeadsFromApi.length > 0 &&
                          filteredCrewLeadOptions.length === 0 &&
                          filteredRemovableCrewLeadOptions.length === 0 && (
                            <p className="text-sm text-gray-500">
                              No crew lead matches that search.
                            </p>
                          )}
                        {!crewLeadsLoading &&
                          !crewLeadsError &&
                          crewLeadsFromApi.length > 0 &&
                          (filteredCrewLeadOptions.length > 0 ||
                            filteredRemovableCrewLeadOptions.length > 0 ||
                            !crewLeadSearchQuery.trim()) && (
                            <div className="space-y-4">
                              <div>
                                <p className="text-xs font-medium text-gray-700 mb-2">
                                  Add
                                </p>
                                {availableCrewLeadOptions.length === 0 ? (
                                  <p className="text-sm text-gray-500">
                                    All crew leads are already on this timesheet.
                                  </p>
                                ) : filteredCrewLeadOptions.length === 0 ? (
                                  <p className="text-sm text-gray-500">
                                    No available crew lead matches that search.
                                  </p>
                                ) : (
                                  <div className="space-y-2">
                                    {filteredCrewLeadOptions.map((m) => {
                                      const id = String(m.id);
                                      const checked =
                                        selectedCrewLeadIds.includes(id);
                                      return (
                                        <label
                                          key={id}
                                          className="flex gap-2 items-center cursor-pointer"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={(e) => {
                                              setSelectedCrewLeadIds((prev) => {
                                                const set = new Set(prev || []);
                                                if (e.target.checked) set.add(id);
                                                else set.delete(id);
                                                return Array.from(set);
                                              });
                                            }}
                                            className="rounded border-gray-300"
                                          />
                                          <span className="text-sm text-gray-800 truncate">
                                            {m.name || 'Unknown'}
                                          </span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                              <div className="border-t border-gray-100 pt-3">
                                <p className="text-xs font-medium text-gray-700 mb-2">
                                  Remove
                                </p>
                                {removableCrewLeadOptions.length === 0 ? (
                                  <p className="text-sm text-gray-500">
                                    No crews have been added yet.
                                  </p>
                                ) : filteredRemovableCrewLeadOptions.length === 0 ? (
                                  <p className="text-sm text-gray-500">
                                    No added crew lead matches that search.
                                  </p>
                                ) : (
                                  <div className="space-y-2">
                                    {filteredRemovableCrewLeadOptions.map((m) => {
                                      const id = String(m.id);
                                      const checked = (
                                        selectedCrewLeadIdsToRemove || []
                                      ).includes(id);
                                      return (
                                        <label
                                          key={id}
                                          className="flex gap-2 items-center cursor-pointer"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={(e) => {
                                              setSelectedCrewLeadIdsToRemove(
                                                (prev) => {
                                                  const set = new Set(prev || []);
                                                  if (e.target.checked) set.add(id);
                                                  else set.delete(id);
                                                  return Array.from(set);
                                                }
                                              );
                                            }}
                                            className="rounded border-gray-300"
                                          />
                                          <span className="text-sm text-gray-800 truncate">
                                            {m.name || 'Unknown'}
                                          </span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                      </div>
                      <div className="px-4 py-3 border-t border-gray-200">
                        <button
                          type="button"
                          onClick={requestCrewLeadsUpdate}
                          disabled={
                            isUpdatingCrewLeads ||
                            (selectedCrewLeadsToAdd.length === 0 &&
                              selectedCrewLeadsToRemove.length === 0)
                          }
                          className="h-9 w-full rounded-md bg-[#AD2226] text-sm font-medium text-white hover:bg-[#8F1B1E] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {isUpdatingCrewLeads ? 'Updating...' : 'Update'}
                        </button>
                      </div>
                    </div>
                  </>,
                  document.body
                )}
            </div>

            <div className="relative min-w-0">
              <button
                ref={addEmployeeBtnRef}
                type="button"
                onClick={() => {
                  setIsAddLaborOpen(false);
                  setIsAddCrewOpen(false);
                  setIsCopyEmployeesOpen(false);
                  setIsAddEmployeeOpen((v) => !v);
                }}
                className="h-11 md:h-10 w-full md:w-auto px-4 md:px-6 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 touch-manipulation"
              >
                Add Employee
              </button>

              {isAddEmployeeOpen &&
                addEmployeePopupPos &&
                createPortal(
                  <>
                    <div
                      className="fixed inset-0 z-[10240]"
                      onClick={() => setIsAddEmployeeOpen(false)}
                    />
                    <div
                      className="fixed z-[10250] rounded-lg bg-white border border-gray-200 shadow-xl"
                      style={{
                        left: addEmployeePopupPos.left,
                        bottom: addEmployeePopupPos.bottom,
                        width: addEmployeePopupPos.width,
                      }}
                    >
                      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900">
                            Add Employee
                          </h3>
                          {!teamMembersLoading &&
                            !teamMembersError &&
                            teamMembersFromApi.length > 0 && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              Total:{' '}
                              <span className="font-semibold text-gray-700">
                                {teamMembersFromApi.length}
                              </span>
                              {employeeSearchQuery.trim()
                                ? ` · Showing ${filteredEmployeeOptions.length}`
                                : ' with crew'}
                              {availableEmployeeOptions.length !==
                                teamMembersFromApi.length &&
                                !employeeSearchQuery.trim() && (
                                  <span>
                                    {' '}
                                    · {availableEmployeeOptions.length} available
                                  </span>
                                )}
                            </p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => setIsAddEmployeeOpen(false)}
                          className="text-gray-400 hover:text-gray-600"
                          aria-label="Close"
                        >
                          ✕
                        </button>
                      </div>
                      <div className="px-4 py-2 border-b border-gray-100">
                        <input
                          type="search"
                          value={employeeSearchQuery}
                          onChange={(e) => setEmployeeSearchQuery(e.target.value)}
                          placeholder="Search by name or employee ID…"
                          className="w-full h-9 px-3 rounded-md border border-gray-300 text-sm"
                        />
                      </div>
                      <div className="px-4 py-3 max-h-[min(280px,42dvh)] overflow-y-auto overscroll-contain">
                        {teamMembersLoading && (
                          <p className="text-sm text-gray-500">Loading team members…</p>
                        )}
                        {teamMembersError && (
                          <p className="text-sm text-red-600">{teamMembersError}</p>
                        )}
                        {!teamMembersLoading &&
                          !teamMembersError &&
                          teamMembersFromApi.length === 0 && (
                            <p className="text-sm text-red-600">
                              Employee list is temporarily unavailable. Please try again.
                            </p>
                          )}
                        {!teamMembersLoading &&
                          !teamMembersError &&
                          teamMembersFromApi.length > 0 &&
                          filteredEmployeeOptions.length === 0 && (
                            <p className="text-sm text-gray-500">
                              No employee matches that search.
                            </p>
                          )}
                        {!teamMembersLoading &&
                          !teamMembersError &&
                          filteredEmployeeOptions.map((m) => {
                            const id = String(m.id);
                            const checked = selectedEmployeeIds.includes(id);
                            return (
                              <label
                                key={id}
                                className="flex items-center gap-2 py-1.5 text-sm text-gray-800 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() =>
                                    setSelectedEmployeeIds((prev) =>
                                      checked
                                        ? prev.filter((x) => x !== id)
                                        : [...prev, id]
                                    )
                                  }
                                />
                                <span className="truncate">{m.name || 'Unknown'}</span>
                              </label>
                            );
                          })}
                      </div>
                      <div className="px-4 py-3 border-t border-gray-200">
                        <button
                          type="button"
                          onClick={handleAddEmployeesUpdate}
                          disabled={
                            isAddingEmployees || selectedEmployeesToAdd.length === 0
                          }
                          className="h-9 w-full rounded-md bg-[#AD2226] text-sm font-medium text-white hover:bg-[#8F1B1E] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {isAddingEmployees ? 'Updating...' : 'Update'}
                        </button>
                      </div>
                    </div>
                  </>,
                  document.body
                )}
            </div>

            <div className="relative col-span-2 min-w-0 md:col-auto">
              <button
                ref={addLaborBtnRef}
                type="button"
                onClick={() => {
                  setIsAddEmployeeOpen(false);
                  setIsAddCrewOpen(false);
                  setIsCopyEmployeesOpen(false);
                  setIsAddLaborOpen((v) => !v);
                }}
                className="h-11 md:h-10 w-full md:w-auto px-4 md:px-6 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 touch-manipulation"
              >
                Add/Remove Labor Type
              </button>

              {isAddLaborOpen &&
                addLaborPopupPos &&
                createPortal(
                  <>
                    <div
                      className="fixed inset-0 z-[10240]"
                      onClick={() => setIsAddLaborOpen(false)}
                    />
                    <div
                      className="fixed z-[10250] rounded-lg bg-white border border-gray-200 shadow-xl"
                      style={{
                        left: addLaborPopupPos.left,
                        bottom: addLaborPopupPos.bottom,
                        width: addLaborPopupPos.width,
                      }}
                    >
                      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                        <h3 className="text-sm font-semibold text-gray-900">
                          Add/Remove Labor Type
                        </h3>
                        <button
                          type="button"
                          onClick={() => setIsAddLaborOpen(false)}
                          className="text-gray-400 hover:text-gray-600"
                          aria-label="Close"
                        >
                          ✕
                        </button>
                      </div>
                      <div className="px-4 py-3 max-h-[min(360px,50dvh)] overflow-y-auto overscroll-contain">
                        {laborScopeCodesLoading && (
                          <p className="text-sm text-gray-500">Loading labor types…</p>
                        )}
                        {laborScopeCodesError && (
                          <p className="text-sm text-red-600">{laborScopeCodesError}</p>
                        )}
                        {!laborScopeCodesLoading &&
                          !laborScopeCodesError &&
                          !scopeName && (
                            <p className="text-sm text-gray-500">No scope selected.</p>
                          )}
                        {!laborScopeCodesLoading &&
                          !laborScopeCodesError &&
                          scopeName &&
                          !scopeEntry &&
                          removableLaborColumns.length === 0 && (
                            <p className="text-sm text-gray-500">
                              No labor types defined for this scope.
                            </p>
                          )}
                        {!laborScopeCodesLoading &&
                          !laborScopeCodesError &&
                          scopeName &&
                          (scopeEntry || removableLaborColumns.length > 0) && (
                            <div className="space-y-4">
                              <div>
                                <p className="text-xs font-medium text-gray-700 mb-2">
                                  Add
                                </p>
                                {availableDescriptionOptions.length === 0 ? (
                                  <p className="text-sm text-gray-500">
                                    All labor types for this scope are already in the
                                    table.
                                  </p>
                                ) : (
                                  <div className="space-y-2">
                                    {availableDescriptionOptions.map((opt) => (
                                      <label
                                        key={opt.id}
                                        className="flex gap-2 items-center cursor-pointer"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={(selectedLaborTypes || []).includes(
                                            opt.id
                                          )}
                                          onChange={(e) => {
                                            setSelectedLaborTypes((prev) => {
                                              const set = new Set(prev || []);
                                              if (e.target.checked) set.add(opt.id);
                                              else set.delete(opt.id);
                                              return Array.from(set);
                                            });
                                          }}
                                          className="rounded border-gray-300"
                                        />
                                        <span className="text-sm text-gray-800">
                                          {opt.label}
                                        </span>
                                      </label>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="border-t border-gray-100 pt-3">
                                <p className="text-xs font-medium text-gray-700 mb-2">
                                  Remove
                                </p>
                                {removableLaborColumns.length === 0 ? (
                                  <p className="text-sm text-gray-500">
                                    No additional labor types have been added.
                                  </p>
                                ) : (
                                  <div className="space-y-2">
                                    {removableLaborColumns.map((col) => (
                                      <label
                                        key={col.id}
                                        className="flex gap-2 items-center cursor-pointer"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={(
                                            selectedLaborTypesToRemove || []
                                          ).includes(col.id)}
                                          onChange={(e) => {
                                            setSelectedLaborTypesToRemove((prev) => {
                                              const set = new Set(prev || []);
                                              if (e.target.checked) set.add(col.id);
                                              else set.delete(col.id);
                                              return Array.from(set);
                                            });
                                          }}
                                          className="rounded border-gray-300"
                                        />
                                        <span className="text-sm text-gray-800">
                                          {col.label}
                                        </span>
                                      </label>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                      </div>
                      <div className="px-4 py-3 border-t border-gray-200">
                        <button
                          type="button"
                          onClick={handleLaborTypesUpdate}
                          disabled={
                            isUpdatingLaborTypes ||
                            (selectedDescriptionRows.length === 0 &&
                              selectedRemovableLaborColumns.length === 0)
                          }
                          className="w-full h-9 rounded-md bg-[#AD2226] text-white text-sm font-medium hover:bg-[#8F1B1E] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {isUpdatingLaborTypes ? 'Updating...' : 'Update'}
                        </button>
                      </div>
                    </div>
                  </>,
                  document.body
                )}
            </div>

            <div className="relative col-span-2 min-w-0 md:col-auto">
              <button
                ref={copyEmployeesBtnRef}
                type="button"
                onClick={() => {
                  setIsAddEmployeeOpen(false);
                  setIsAddCrewOpen(false);
                  setIsAddLaborOpen(false);
                  setIsCopyEmployeesOpen((v) => !v);
                }}
                disabled={isCopyingEmployees}
                className="h-11 md:h-10 w-full md:w-auto px-4 md:px-6 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-60 touch-manipulation"
              >
                {isCopyingEmployees
                  ? 'Copying…'
                  : 'Copy Employees from Another Date'}
              </button>

              {isCopyEmployeesOpen &&
                addCopyEmployeesPopupPos &&
                createPortal(
                  <>
                    <div
                      className="fixed inset-0 z-[10240]"
                      onClick={() => {
                        if (!isCopyingEmployees) setIsCopyEmployeesOpen(false);
                      }}
                    />
                    <div
                      className="fixed z-[10250] rounded-lg bg-white border border-gray-200 shadow-xl p-3"
                      style={{
                        left: Math.min(
                          addCopyEmployeesPopupPos.left,
                          Math.max(8, window.innerWidth - 308)
                        ),
                        bottom: addCopyEmployeesPopupPos.bottom,
                        width: 300,
                      }}
                    >
                      {savedEmployeeDatesLoading && (
                        <p className="text-sm text-gray-500 px-1 py-2">
                          Loading calendar…
                        </p>
                      )}
                      {savedEmployeeDatesError && (
                        <p className="text-sm text-red-600 px-1 py-2">
                          {savedEmployeeDatesError}
                        </p>
                      )}
                      {!savedEmployeeDatesLoading &&
                        !savedEmployeeDatesError &&
                        savedEmployeeDates.length === 0 && (
                          <p className="text-sm text-gray-500 px-1 py-2">
                            No other saved employee lists found for this activity.
                          </p>
                        )}
                      {!savedEmployeeDatesLoading &&
                        !savedEmployeeDatesError &&
                        savedEmployeeDates.length > 0 && (
                          <CrewCopyFromDateCalendar
                            selectedDate={selectedCopyFromDate}
                            availableDates={savedEmployeeDates}
                            excludeDate={workDate}
                            disabled={isCopyingEmployees}
                            onSelect={(iso) => {
                              handleCopyEmployeesFromDate(iso);
                            }}
                          />
                        )}
                    </div>
                  </>,
                  document.body
                )}
            </div>
          </div>

          {/* Travel Reimbursement — same modal, below Crew Time Sheet */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex flex-col gap-3 mb-3 md:flex-row md:items-end md:justify-between">
              <div className="min-w-0 md:max-w-[280px] lg:max-w-none">
                <h3 className="text-base font-semibold text-gray-800">
                  Travel Reimbursement
                </h3>
                <p className="text-xs text-gray-500 mt-0.5 leading-snug">
                  <span className="font-medium text-[#1E5A8A]">
                    {employees.length}{' '}
                    {employees.length === 1 ? 'employee' : 'employees'}
                  </span>
                  <span className="mx-1.5 text-gray-300" aria-hidden>
                    ·
                  </span>
                  Mileage and per diem for the same crew on this work date.
                </p>
              </div>
              <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-end sm:justify-end sm:gap-3 md:w-auto">
                <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:gap-3">
                  <div className="min-w-0">
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Branch
                    </label>
                    <div className="h-10 sm:h-9 w-full sm:w-[160px] px-3 flex items-center rounded-md border border-gray-200 bg-white text-sm text-gray-900 truncate">
                      {travelBranch}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Miles From Branch
                    </label>
                    <div className="h-10 sm:h-9 w-full sm:w-[120px] px-3 flex items-center rounded-md border border-gray-200 bg-white text-sm text-gray-900">
                      {travelMilesLoading ? '…' : milesFromBranch}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  disabled={employees.length === 0}
                  onClick={() => setIsTravelUpdateMultipleOpen(true)}
                  className="flex h-10 sm:h-9 items-center justify-end gap-2 self-end shrink-0 text-[15px] sm:text-[16px] font-normal text-[#AD2226] underline font-poppins leading-none focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline touch-manipulation"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    className="w-6 h-6"
                    fill="none"
                    aria-hidden
                  >
                    <path
                      d="M10 6C10 7.10457 10.8954 8 12 8C13.1046 8 14 7.10457 14 6C14 4.89543 13.1046 4 12 4C10.8954 4 10 4.89543 10 6Z"
                      fill="#AD2226"
                    />
                    <path
                      d="M10 12C10 13.1046 10.8954 14 12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12Z"
                      fill="#AD2226"
                    />
                    <path
                      d="M12 20C10.8954 20 10 19.1046 10 18C10 16.8954 10.8954 16 12 16C13.1046 16 14 16.8954 14 18C14 19.1046 13.1046 20 12 20Z"
                      fill="#AD2226"
                    />
                  </svg>
                  <span>Update Multiple</span>
                </button>
              </div>
            </div>

            <PinnedColumnHScroll measureKey={`travel-${employees.length}`}>
              <table className="w-full min-w-max border-separate border-spacing-0 text-sm">
                <thead>
                  <tr className="bg-white">
                    <th
                      rowSpan={2}
                      className={`${stickyTravelEmployeeCol} bg-white px-3 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 border-b border-r border-gray-200`}
                    >
                      Employee
                    </th>
                    <th
                      rowSpan={2}
                      className="bg-white px-3 py-3 text-left text-xs font-semibold text-gray-600 border-b border-r border-gray-200 min-w-[120px]"
                    >
                      Work Location
                    </th>
                    <th
                      rowSpan={2}
                      className="bg-[#F5F0E8] px-2 py-3 text-center text-[11px] font-semibold text-gray-600 border-b border-r border-gray-200 min-w-[88px] leading-tight"
                    >
                      Override
                      <br />
                      Default Miles
                    </th>
                    <th
                      rowSpan={2}
                      className="bg-[#F5F0E8] px-2 py-3 text-center text-xs font-semibold text-gray-600 border-b border-r border-gray-200 min-w-[72px]"
                    >
                      Miles
                    </th>
                    <th
                      colSpan={MILEAGE_REIMBURSEMENT_TYPES.length}
                      className="bg-[#D6E8F5] px-2 py-2 text-center text-xs font-bold text-[#1E5A8A] border-b border-r border-gray-200"
                    >
                      Mileage Reimbursement
                    </th>
                    <th
                      colSpan={PER_DIEM_TYPES.length}
                      className="bg-[#F5E6D3] px-2 py-2 text-center text-xs font-bold text-[#8B5A2B] border-b border-r border-gray-200"
                    >
                      Per Diem
                    </th>
                    <th
                      rowSpan={2}
                      className="bg-white px-3 py-3 text-center text-xs font-semibold text-gray-600 border-b border-gray-200 min-w-[88px]"
                    >
                      Amount
                    </th>
                  </tr>
                  <tr className="bg-white">
                    {MILEAGE_REIMBURSEMENT_TYPES.map((col, idx) => (
                      <th
                        key={col.id}
                        className={`bg-[#EAF4FB] px-2 py-2 text-center text-[11px] font-medium text-gray-700 border-b leading-tight ${
                          idx === MILEAGE_REIMBURSEMENT_TYPES.length - 1
                            ? 'border-r border-gray-200'
                            : ''
                        } min-w-[92px]`}
                      >
                        {col.label}
                      </th>
                    ))}
                    {PER_DIEM_TYPES.map((col, idx) => (
                      <th
                        key={col.id}
                        className={`bg-[#FAF0E4] px-2 py-2 text-center text-[11px] font-medium text-gray-700 border-b leading-tight ${
                          idx === PER_DIEM_TYPES.length - 1
                            ? 'border-r border-gray-200'
                            : ''
                        } min-w-[92px]`}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {employees.length === 0 && (
                    <tr>
                      <td
                        colSpan={
                          4 +
                          MILEAGE_REIMBURSEMENT_TYPES.length +
                          PER_DIEM_TYPES.length +
                          1
                        }
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        No employees yet. Add employees in Crew Time Sheet above.
                      </td>
                    </tr>
                  )}
                  {employees.map((emp, rowIdx) => {
                    const empId = String(emp.id);
                    const defaultMiles = getEmployeeDefaultMiles(empId);
                    const workLocation =
                      emp.workLocation ||
                      workLocationByEmployeeId[empId] ||
                      '—';
                    const travel =
                      travelByEmployeeId[empId] ||
                      buildEmptyTravelEntry(defaultMiles);
                    const bandBg = rowIdx % 2 === 0 ? 'bg-white' : 'bg-slate-100';
                    return (
                      <tr
                        key={`travel-${emp.id}`}
                        className={`border-t border-gray-100 ${bandBg}`}
                      >
                        <td
                          className={`${stickyTravelEmployeeCol} px-3 sm:px-4 py-2.5 border-r border-gray-200 ${bandBg}`}
                        >
                          <span className="truncate font-medium text-[#2C2C2C]">
                            {emp.name}
                          </span>
                        </td>
                        <td
                          className={`px-3 py-2.5 border-r border-gray-200 text-sm text-gray-700 whitespace-nowrap ${bandBg}`}
                        >
                          {workLocation}
                        </td>
                        <td className="px-2 py-2.5 text-center border-r border-gray-200">
                          <input
                            type="checkbox"
                            checked={Boolean(travel.overrideDefaultMiles)}
                            onChange={(e) =>
                              handleTravelOverrideChange(emp.id, e.target.checked)
                            }
                            className="h-[18px] w-[18px] rounded border-gray-300 text-blue-600 focus:ring-blue-500 touch-manipulation"
                            aria-label={`Override default miles for ${emp.name}`}
                          />
                        </td>
                        <td className="px-2 py-2.5 text-center border-r border-gray-200">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={travel.miles ?? defaultMiles}
                            readOnly={!travel.overrideDefaultMiles}
                            onChange={(e) =>
                              handleTravelMilesChange(emp.id, e.target.value)
                            }
                            onFocus={(e) => {
                              if (travel.overrideDefaultMiles) e.target.select();
                            }}
                            className={`mx-auto h-10 w-full max-w-[72px] rounded-md border border-gray-300 px-2 text-center text-sm text-[#2C2C2C] touch-manipulation focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                              travel.overrideDefaultMiles
                                ? 'bg-white'
                                : 'bg-gray-50 text-gray-600 cursor-default'
                            }`}
                            aria-label={`Miles for ${emp.name}`}
                          />
                        </td>
                        {MILEAGE_REIMBURSEMENT_TYPES.map((col, idx) => (
                          <td
                            key={col.id}
                            className={`px-2 py-2.5 text-center ${
                              idx === MILEAGE_REIMBURSEMENT_TYPES.length - 1
                                ? 'border-r border-gray-200'
                                : ''
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={Boolean(travel.flags?.[col.id])}
                              onChange={(e) =>
                                handleTravelFlagChange(
                                  emp.id,
                                  col.id,
                                  e.target.checked
                                )
                              }
                              className="h-[18px] w-[18px] rounded border-gray-300 text-blue-600 focus:ring-blue-500 touch-manipulation"
                              aria-label={`${col.label} for ${emp.name}`}
                            />
                          </td>
                        ))}
                        {PER_DIEM_TYPES.map((col, idx) => (
                          <td
                            key={col.id}
                            className={`px-2 py-2.5 text-center ${
                              idx === PER_DIEM_TYPES.length - 1
                                ? 'border-r border-gray-200'
                                : ''
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={Boolean(travel.flags?.[col.id])}
                              onChange={(e) =>
                                handleTravelFlagChange(
                                  emp.id,
                                  col.id,
                                  e.target.checked
                                )
                              }
                              className="h-[18px] w-[18px] rounded border-gray-300 text-blue-600 focus:ring-blue-500 touch-manipulation"
                              aria-label={`${col.label} for ${emp.name}`}
                            />
                          </td>
                        ))}
                        <td className="px-3 py-3 text-center text-sm font-semibold text-[#2563EB]">
                          {formatTravelAmount(travel.amount)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </PinnedColumnHScroll>
          </div>
              </div>
            </div>

            <div className="shrink-0 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 bg-white pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <div className="flex flex-col items-center gap-2">
            {message && (
              <p
                className={`text-sm text-center px-1 ${
                  message.type === 'success' ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {message.text}
              </p>
            )}
            <div className="flex w-full flex-nowrap sm:flex-wrap items-center justify-center gap-3">
            {usingFlowNav && (
              <DailyLogBackButton
                currentStep="crew"
                onStepChange={goToFlowStep}
                disabled={saving}
              />
            )}
            <button
              type="button"
              onClick={handleSaveToMiter}
              disabled={
                saving ||
                (employees.length > 0 && (jobLoading || !isMiterActivity))
              }
              className={`h-12 sm:h-11 min-w-0 flex-1 sm:flex-none sm:w-auto sm:max-w-none sm:px-16 rounded-md text-white text-sm font-semibold touch-manipulation ${
                saving ||
                (employees.length > 0 && (jobLoading || !isMiterActivity))
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-[#AD2226] hover:bg-[#8F1B1E]'
              }`}
            >
              {saving ? 'Saving…' : 'Save and Log Equipment'}
            </button>
            </div>
          </div>
        </div>
            </div>
      </div>
    </div>,
    document.body
      )}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={isBulkDelete ? 'Delete Employees' : 'Delete Employee'}
        message={deleteConfirmMessage}
        confirmLabel={isBulkDelete ? 'Delete selected' : 'Delete'}
        cancelLabel="Cancel"
        onConfirm={handleConfirmRemoveEmployee}
        onClose={handleCloseDeleteConfirm}
        isLoading={Boolean(deletingEmployeeId)}
        isDestructive
        overlayClassName="z-[10200]"
      />
      <ConfirmDialog
        isOpen={crewRemoveConfirm.isOpen}
        title={
          crewRemoveConfirm.employeeCount > 1
            ? 'Delete Employees'
            : 'Delete Employee'
        }
        message={
          crewRemoveConfirm.employeeCount > 1
            ? `Are you sure you want to delete ${crewRemoveConfirm.employeeCount} employees from this crew timesheet? Matching Miter timesheets for this work date will be archived, and saved Travel Reimbursement entries for those employees will be deleted from Miter.`
            : `Are you sure you want to delete ${
                crewRemoveConfirm.crewsToRemove?.[0]?.name || 'this employee'
              } from this crew timesheet? Matching Miter timesheets for this work date will be archived, and saved Travel Reimbursement entries for this employee will be deleted from Miter.`
        }
        confirmLabel={
          crewRemoveConfirm.employeeCount > 1 ? 'Delete selected' : 'Delete'
        }
        cancelLabel="Cancel"
        onConfirm={handleConfirmCrewRemove}
        onClose={handleCloseCrewRemoveConfirm}
        isLoading={isUpdatingCrewLeads}
        isDestructive
        overlayClassName="z-[10200]"
      />
      <CrewTimesheetUpdateMultipleModal
        isOpen={isUpdateMultipleOpen}
        onClose={() => setIsUpdateMultipleOpen(false)}
        employees={employees}
        laborColumns={allHourColumns}
        onUpdateHours={handleUpdateMultipleHours}
      />
      <CrewTravelUpdateMultipleModal
        isOpen={isTravelUpdateMultipleOpen}
        onClose={() => setIsTravelUpdateMultipleOpen(false)}
        employees={employees}
        mileageTypes={MILEAGE_REIMBURSEMENT_TYPES}
        perDiemTypes={PER_DIEM_TYPES}
        defaultMiles={milesFromBranch}
        onApply={handleTravelBulkUpdate}
      />
      <ModalEquipmentTimeCard
        isOpen={equipmentOpen}
        onClose={() => {
          if (!usingFlowNav) setIsEquipmentTimeCardOpen(false);
          onClose?.();
        }}
        onFlowNavigate={usingFlowNav ? goToFlowStep : undefined}
        projectCode={projectCode || ''}
        projectName={displayProjectName}
        workDate={workDate}
        onWorkDateChange={setWorkDate}
        activity={displayActivity}
        scopeName={scopeName || ''}
        scopeId={scopeId || ''}
        onLogProduction={() => {
          if (usingFlowNav) {
            goToFlowStep('production');
            return;
          }
          setIsEquipmentTimeCardOpen(false);
          onLogProduction(buildFlowContext());
          onClose?.();
        }}
      />
    </>
  );
};

export default ModalCrewTimesheet;
